import React, { useEffect, useState } from 'react';
import { adminApi } from '../lib/adminApi';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';
import { Settings as SettingsIcon, Save, Send, MessageCircle, Mail, KeyRound, ExternalLink, Layout, Image as ImageIcon, Search as SearchIcon, Upload, CreditCard, Compass, Smartphone, HardDrive, Map as MapIcon, Copy, RefreshCw, Sparkles, Home, Plus, Trash2 } from 'lucide-react';
import { Switch } from '../components/ui/switch';

const SecretInput = ({ id, value, onChange, hint, ...rest }) => (
  <div>
    <Input id={id} value={value} onChange={onChange} {...rest} />
    {hint && (
      <div className="text-xs text-gray-500 mt-1" data-testid={`${id}-hint`}>
        Saved value: <span className="font-mono">{hint}</span>{' '}
        <span className="text-gray-400">— leave blank to keep unchanged</span>
      </div>
    )}
  </div>
);

const Field = ({ label, htmlFor, children, hint }) => (
  <div>
    <Label htmlFor={htmlFor} className="text-sm">{label}</Label>
    <div className="mt-1">{children}</div>
    {hint && <p className="text-xs text-gray-500 mt-1">{hint}</p>}
  </div>
);

const Msg91Form = () => {
  const { toast } = useToast();
  const [form, setForm] = useState({
    auth_key: '', template_id: '', flow_id: '', flow_var: 'message', sender_id: 'TGMART',
  });
  const [hints, setHints] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testTo, setTestTo] = useState('');
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await adminApi.getSettings('msg91');
        const v = r.value || {};
        setForm({
          auth_key: '',
          template_id: v.template_id || '',
          flow_id: v.flow_id || '',
          flow_var: v.flow_var || 'message',
          sender_id: v.sender_id || 'TGMART',
        });
        setHints({ auth_key: v.auth_key_hint || '' });
      } catch (e) {
        toast({ title: 'Failed to load MSG91 settings', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
      } finally { setLoading(false); }
    })();
    // Load once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async (e) => {
    e?.preventDefault?.();
    setSaving(true);
    try {
      const payload = { ...form };
      // empty auth_key => keep existing
      const r = await adminApi.saveSettings('msg91', payload);
      setHints({ auth_key: r.value?.auth_key_hint || '' });
      setForm((f) => ({ ...f, auth_key: '' }));
      toast({ title: 'MSG91 settings saved' });
    } catch (e) {
      toast({ title: 'Save failed', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    } finally { setSaving(false); }
  };

  const sendTest = async () => {
    if (!/^[0-9]{10}$/.test(testTo)) {
      toast({ title: 'Enter a 10-digit mobile', variant: 'destructive' });
      return;
    }
    setTesting(true);
    try {
      const r = await adminApi.testMsg91(testTo);
      toast({
        title: r.ok ? 'Test SMS sent' : 'Test SMS not sent',
        description: r.detail || '',
        variant: r.ok ? 'default' : 'destructive',
      });
    } catch (e) {
      toast({ title: 'Test failed', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    } finally { setTesting(false); }
  };

  if (loading) return <div className="text-gray-500">Loading MSG91 settings...</div>;

  return (
    <form onSubmit={save} className="space-y-5" data-testid="site-settings-msg91-form">
      <div className="grid md:grid-cols-2 gap-4">
        <Field label="Auth Key *" htmlFor="msg91_auth_key" hint="Found under MSG91 → Settings → API Keys.">
          <SecretInput
            id="msg91_auth_key"
            placeholder={hints.auth_key ? '•••• keep unchanged' : 'Paste your MSG91 auth key'}
            value={form.auth_key}
            hint={hints.auth_key}
            onChange={(e) => setForm({ ...form, auth_key: e.target.value })}
            data-testid="msg91-auth-key-input"
          />
        </Field>
        <Field label="Sender ID" htmlFor="msg91_sender_id" hint="6-character DLT-approved sender, e.g. TGMART.">
          <Input id="msg91_sender_id" value={form.sender_id} maxLength={6}
            onChange={(e) => setForm({ ...form, sender_id: e.target.value.toUpperCase() })}
            data-testid="msg91-sender-id-input" />
        </Field>
        <Field label="OTP Template ID" htmlFor="msg91_template_id" hint="DLT-approved OTP template ID used for login OTPs.">
          <Input id="msg91_template_id" value={form.template_id}
            onChange={(e) => setForm({ ...form, template_id: e.target.value })}
            data-testid="msg91-template-id-input" />
        </Field>
        <Field label="Flow ID (transactional SMS)" htmlFor="msg91_flow_id" hint="MSG91 Flow used for transactional SMS (welcome, alerts).">
          <Input id="msg91_flow_id" value={form.flow_id}
            onChange={(e) => setForm({ ...form, flow_id: e.target.value })}
            data-testid="msg91-flow-id-input" />
        </Field>
        <Field label="Flow Variable Name" htmlFor="msg91_flow_var" hint="The variable inside your Flow that receives the message body. Default: message">
          <Input id="msg91_flow_var" value={form.flow_var}
            onChange={(e) => setForm({ ...form, flow_var: e.target.value })}
            data-testid="msg91-flow-var-input" />
        </Field>
      </div>

      <div className="flex flex-wrap gap-3 items-center pt-2 border-t">
        <Button type="submit" disabled={saving} className="bg-[#ee5e23] hover:bg-[#d54d18]" data-testid="msg91-save-btn">
          <Save className="w-4 h-4 mr-2" />{saving ? 'Saving...' : 'Save MSG91 settings'}
        </Button>
        <div className="flex items-center gap-2 ml-auto">
          <Input
            placeholder="10-digit mobile for test"
            value={testTo}
            maxLength={10}
            onChange={(e) => setTestTo(e.target.value.replace(/\D/g, ''))}
            className="w-48"
            data-testid="msg91-test-to-input"
          />
          <Button type="button" variant="outline" disabled={testing} onClick={sendTest} data-testid="msg91-test-btn">
            <Send className="w-4 h-4 mr-2" />{testing ? 'Sending...' : 'Send test SMS'}
          </Button>
        </div>
      </div>
    </form>
  );
};

const EmailForm = () => {
  const { toast } = useToast();
  const [form, setForm] = useState({
    enabled: false,
    provider: 'smtp',
    from_address: '',
    from_name: 'TelanganaMart',
    admin_notify_email: '',
    smtp_host: '',
    smtp_port: '587',
    smtp_user: '',
    smtp_password: '',
    use_tls: true,
    sendgrid_api_key: '',
  });
  const [hints, setHints] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testTo, setTestTo] = useState('');
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await adminApi.getSettings('email');
        const v = r.value || {};
        setForm({
          enabled: String(v.enabled ?? '').toLowerCase() === 'true' || v.enabled === true,
          provider: v.provider || 'smtp',
          from_address: v.from_address || '',
          from_name: v.from_name || 'TelanganaMart',
          admin_notify_email: v.admin_notify_email || '',
          smtp_host: v.smtp_host || '',
          smtp_port: String(v.smtp_port || '587'),
          smtp_user: v.smtp_user || '',
          smtp_password: '',
          use_tls: String(v.use_tls ?? 'true').toLowerCase() !== 'false',
          sendgrid_api_key: '',
        });
        setHints({
          smtp_password: v.smtp_password_hint || '',
          sendgrid_api_key: v.sendgrid_api_key_hint || '',
        });
      } catch (e) {
        toast({ title: 'Failed to load email settings', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
      } finally { setLoading(false); }
    })();
    // Load once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async (e) => {
    e?.preventDefault?.();
    setSaving(true);
    try {
      const payload = {
        ...form,
        enabled: form.enabled ? 'true' : 'false',
        use_tls: form.use_tls ? 'true' : 'false',
        smtp_port: String(form.smtp_port || '587'),
      };
      const r = await adminApi.saveSettings('email', payload);
      setHints({
        smtp_password: r.value?.smtp_password_hint || '',
        sendgrid_api_key: r.value?.sendgrid_api_key_hint || '',
      });
      setForm((f) => ({ ...f, smtp_password: '', sendgrid_api_key: '' }));
      toast({ title: 'Email settings saved' });
    } catch (e) {
      toast({ title: 'Save failed', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    } finally { setSaving(false); }
  };

  const sendTest = async () => {
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(testTo)) {
      toast({ title: 'Enter a valid email address', variant: 'destructive' });
      return;
    }
    setTesting(true);
    try {
      const r = await adminApi.testEmail(testTo);
      toast({
        title: r.ok ? 'Test email sent' : 'Test email not sent',
        description: r.detail || '',
        variant: r.ok ? 'default' : 'destructive',
      });
    } catch (e) {
      toast({ title: 'Test failed', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    } finally { setTesting(false); }
  };

  if (loading) return <div className="text-gray-500">Loading email settings...</div>;

  const isSmtp = form.provider === 'smtp';
  const isSendgrid = form.provider === 'sendgrid';

  return (
    <form onSubmit={save} className="space-y-5" data-testid="site-settings-email-form">
      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={form.enabled}
          onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
          data-testid="email-enabled-checkbox"
        />
        <span>Enable email notifications</span>
        <span className="text-xs text-gray-500">(when off, emails are logged but not sent)</span>
      </label>

      <Field label="Provider" htmlFor="email_provider">
        <select
          id="email_provider"
          className="w-full border rounded-md px-3 py-2 text-sm"
          value={form.provider}
          onChange={(e) => setForm({ ...form, provider: e.target.value })}
          data-testid="email-provider-select"
        >
          <option value="smtp">SMTP (Gmail / custom server)</option>
          <option value="sendgrid">SendGrid (HTTP API)</option>
          <option value="resend">Resend (coming soon)</option>
        </select>
      </Field>

      <div className="grid md:grid-cols-2 gap-4">
        <Field label="From Address" htmlFor="email_from_address" hint="The visible sender email (must be verified with your provider).">
          <Input id="email_from_address" type="email" value={form.from_address}
            onChange={(e) => setForm({ ...form, from_address: e.target.value })}
            placeholder="no-reply@yourdomain.com"
            data-testid="email-from-address-input" />
        </Field>
        <Field label="From Name" htmlFor="email_from_name">
          <Input id="email_from_name" value={form.from_name}
            onChange={(e) => setForm({ ...form, from_name: e.target.value })}
            data-testid="email-from-name-input" />
        </Field>
      </div>

      <Field label="Admin Notification Email" htmlFor="email_admin_notify" hint="Receives a copy of every new buyer enquiry. Leave blank to disable.">
        <Input id="email_admin_notify" type="email" value={form.admin_notify_email}
          onChange={(e) => setForm({ ...form, admin_notify_email: e.target.value })}
          placeholder="admin@yourdomain.com"
          data-testid="email-admin-notify-input" />
      </Field>

      {isSmtp && (
        <div className="grid md:grid-cols-2 gap-4 p-4 border rounded-md bg-gray-50">
          <Field label="SMTP Host" htmlFor="email_smtp_host" hint="e.g. smtp.gmail.com, smtp.zoho.in">
            <Input id="email_smtp_host" value={form.smtp_host}
              onChange={(e) => setForm({ ...form, smtp_host: e.target.value })}
              data-testid="email-smtp-host-input" />
          </Field>
          <Field label="SMTP Port" htmlFor="email_smtp_port" hint="587 (TLS) or 465 (SSL)">
            <Input id="email_smtp_port" value={form.smtp_port} type="number"
              onChange={(e) => setForm({ ...form, smtp_port: e.target.value })}
              data-testid="email-smtp-port-input" />
          </Field>
          <Field label="SMTP Username" htmlFor="email_smtp_user">
            <Input id="email_smtp_user" value={form.smtp_user}
              onChange={(e) => setForm({ ...form, smtp_user: e.target.value })}
              autoComplete="off"
              data-testid="email-smtp-user-input" />
          </Field>
          <Field label="SMTP Password" htmlFor="email_smtp_password" hint="For Gmail, generate an App Password.">
            <SecretInput
              id="email_smtp_password"
              type="password"
              autoComplete="new-password"
              placeholder={hints.smtp_password ? '•••• keep unchanged' : 'Enter SMTP password / app password'}
              value={form.smtp_password}
              hint={hints.smtp_password}
              onChange={(e) => setForm({ ...form, smtp_password: e.target.value })}
              data-testid="email-smtp-password-input"
            />
          </Field>
          <label className="flex items-center gap-2 text-sm col-span-full">
            <input type="checkbox" checked={form.use_tls}
              onChange={(e) => setForm({ ...form, use_tls: e.target.checked })}
              data-testid="email-use-tls-checkbox" />
            <span>Use STARTTLS (recommended for port 587)</span>
          </label>
        </div>
      )}

      {isSendgrid && (
        <div className="p-4 border rounded-md bg-gray-50">
          <Field label="SendGrid API Key" htmlFor="email_sendgrid_api_key" hint="Create one at sendgrid.com → Settings → API Keys.">
            <SecretInput
              id="email_sendgrid_api_key"
              type="password"
              autoComplete="new-password"
              placeholder={hints.sendgrid_api_key ? '•••• keep unchanged' : 'SG.xxxxxxxx...'}
              value={form.sendgrid_api_key}
              hint={hints.sendgrid_api_key}
              onChange={(e) => setForm({ ...form, sendgrid_api_key: e.target.value })}
              data-testid="email-sendgrid-key-input"
            />
          </Field>
        </div>
      )}

      {form.provider === 'resend' && (
        <div className="p-4 border rounded-md bg-amber-50 text-amber-800 text-sm">
          Resend integration coming soon. For now, please use SMTP or SendGrid.
        </div>
      )}

      <div className="flex flex-wrap gap-3 items-center pt-2 border-t">
        <Button type="submit" disabled={saving} className="bg-[#ee5e23] hover:bg-[#d54d18]" data-testid="email-save-btn">
          <Save className="w-4 h-4 mr-2" />{saving ? 'Saving...' : 'Save email settings'}
        </Button>
        <div className="flex items-center gap-2 ml-auto">
          <Input
            type="email"
            placeholder="recipient@example.com"
            value={testTo}
            onChange={(e) => setTestTo(e.target.value)}
            className="w-64"
            data-testid="email-test-to-input"
          />
          <Button type="button" variant="outline" disabled={testing} onClick={sendTest} data-testid="email-test-btn">
            <Send className="w-4 h-4 mr-2" />{testing ? 'Sending...' : 'Send test email'}
          </Button>
        </div>
      </div>
    </form>
  );
};

const SmsLoginForm = () => {
  const { toast } = useToast();
  const [form, setForm] = useState({
    username: '', api_key: '', sender_id: '', template_id: '',
    otp_message: 'Your TelanganaMart OTP is {otp}. Valid for 5 minutes.',
  });
  const [hints, setHints] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testTo, setTestTo] = useState('');
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await adminApi.getSettings('smslogin');
        const v = r.value || {};
        setForm({
          username: v.username || '',
          api_key: '',
          sender_id: v.sender_id || '',
          template_id: v.template_id || '',
          otp_message: v.otp_message || 'Your TelanganaMart OTP is {otp}. Valid for 5 minutes.',
        });
        setHints({ api_key: v.api_key_hint || '' });
      } catch (e) {
        toast({ title: 'Failed to load SMSLogin settings', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
      } finally { setLoading(false); }
    })();
    // Load once on mount. adminApi is a module-level import (stable) and
    // toast from shadcn's useToast is referentially stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async (e) => {
    e?.preventDefault?.();
    setSaving(true);
    try {
      const r = await adminApi.saveSettings('smslogin', form);
      setHints({ api_key: r.value?.api_key_hint || '' });
      setForm((f) => ({ ...f, api_key: '' }));
      toast({ title: 'SMSLogin settings saved' });
    } catch (e) {
      toast({ title: 'Save failed', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    } finally { setSaving(false); }
  };

  const sendTest = async () => {
    if (!/^[0-9]{10}$/.test(testTo)) {
      toast({ title: 'Enter a 10-digit mobile', variant: 'destructive' });
      return;
    }
    setTesting(true);
    try {
      const r = await adminApi.testSmsLogin(testTo);
      toast({
        title: r.ok ? 'Test SMS sent' : 'Test SMS not sent',
        description: r.detail || '',
        variant: r.ok ? 'default' : 'destructive',
      });
    } catch (e) {
      toast({ title: 'Test failed', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    } finally { setTesting(false); }
  };

  if (loading) return <div className="text-gray-500">Loading SMSLogin settings...</div>;

  return (
    <form onSubmit={save} className="space-y-5" data-testid="site-settings-smslogin-form">
      <div className="p-3 bg-blue-50 border border-blue-200 rounded text-xs text-blue-900">
        <b>SMSLogin.co (Genfon)</b> uses a simple HTTP GET API. Endpoint: <code>https://smslogin.co/v3/api.php</code>.
        For OTP messages, use the placeholder <code>&#123;otp&#125;</code> in the OTP template — it will be replaced with the generated code at send time.
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <Field label="Username *" htmlFor="smslogin_username" hint="The username assigned by SMSLogin (e.g. GENFON).">
          <Input id="smslogin_username" value={form.username}
            onChange={(e) => setForm({ ...form, username: e.target.value })}
            data-testid="smslogin-username-input" />
        </Field>
        <Field label="API Key *" htmlFor="smslogin_api_key" hint="From your SMSLogin dashboard.">
          <SecretInput
            id="smslogin_api_key"
            placeholder={hints.api_key ? '•••• keep unchanged' : 'Paste API key'}
            value={form.api_key}
            hint={hints.api_key}
            onChange={(e) => setForm({ ...form, api_key: e.target.value })}
            data-testid="smslogin-api-key-input"
          />
        </Field>
        <Field label="Sender ID *" htmlFor="smslogin_sender_id" hint="6-character DLT-approved sender (e.g. GENFON).">
          <Input id="smslogin_sender_id" value={form.sender_id} maxLength={6}
            onChange={(e) => setForm({ ...form, sender_id: e.target.value.toUpperCase() })}
            data-testid="smslogin-sender-id-input" />
        </Field>
        <Field label="DLT Template ID" htmlFor="smslogin_template_id" hint="Numeric DLT template ID from the TRAI portal.">
          <Input id="smslogin_template_id" value={form.template_id}
            onChange={(e) => setForm({ ...form, template_id: e.target.value })}
            data-testid="smslogin-template-id-input" />
        </Field>
      </div>
      <Field label="OTP Message Template" htmlFor="smslogin_otp_message"
             hint="Must match your registered DLT template exactly. Use {otp} as a placeholder for the generated code.">
        <Input id="smslogin_otp_message" value={form.otp_message}
          onChange={(e) => setForm({ ...form, otp_message: e.target.value })}
          data-testid="smslogin-otp-message-input" />
      </Field>

      <div className="flex flex-wrap gap-3 items-center pt-2 border-t">
        <Button type="submit" disabled={saving} className="bg-[#ee5e23] hover:bg-[#d54d18]" data-testid="smslogin-save-btn">
          <Save className="w-4 h-4 mr-2" />{saving ? 'Saving...' : 'Save SMSLogin settings'}
        </Button>
        <div className="flex items-center gap-2 ml-auto">
          <Input
            placeholder="10-digit mobile for test"
            value={testTo}
            maxLength={10}
            onChange={(e) => setTestTo(e.target.value.replace(/\D/g, ''))}
            className="w-48"
            data-testid="smslogin-test-to-input"
          />
          <Button type="button" variant="outline" disabled={testing} onClick={sendTest} data-testid="smslogin-test-btn">
            <Send className="w-4 h-4 mr-2" />{testing ? 'Sending...' : 'Send test SMS'}
          </Button>
        </div>
      </div>
    </form>
  );
};

const GoogleOAuthForm = () => {
  const { toast } = useToast();
  const [form, setForm] = useState({ client_id: '', client_secret: '', enabled: true });
  const [hints, setHints] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await adminApi.getSettings('google_oauth');
        const v = r.value || {};
        setForm({
          client_id: v.client_id || '',
          client_secret: '',
          enabled: String(v.enabled ?? 'true').toLowerCase() !== 'false',
        });
        setHints({ client_secret: v.client_secret_hint || '' });
      } catch (e) {
        toast({ title: 'Failed to load Google settings', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
      } finally { setLoading(false); }
    })();
    // Load once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async (e) => {
    e?.preventDefault?.();
    if (!form.client_id.trim()) return toast({ title: 'Client ID is required', variant: 'destructive' });
    setSaving(true);
    try {
      const payload = { ...form, enabled: form.enabled ? 'true' : 'false' };
      const r = await adminApi.saveSettings('google_oauth', payload);
      setHints({ client_secret: r.value?.client_secret_hint || '' });
      setForm((f) => ({ ...f, client_secret: '' }));
      toast({ title: 'Google OAuth settings saved' });
    } catch (e) {
      toast({ title: 'Save failed', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    } finally { setSaving(false); }
  };

  if (loading) return <div className="text-gray-500">Loading Google OAuth settings...</div>;

  const previewOrigin = typeof window !== 'undefined' ? window.location.origin : '';

  return (
    <form onSubmit={save} className="space-y-5" data-testid="site-settings-google-form">
      <div className="p-4 bg-blue-50 border border-blue-200 rounded text-xs text-blue-900 space-y-2">
        <div className="font-semibold flex items-center gap-1">
          <KeyRound className="w-4 h-4" /> Setup instructions
        </div>
        <ol className="list-decimal pl-5 space-y-1">
          <li>Open <a href="https://console.cloud.google.com/apis/credentials" target="_blank" rel="noreferrer" className="text-[#2c54b2] underline inline-flex items-center gap-1">Google Cloud → APIs &amp; Services → Credentials <ExternalLink className="w-3 h-3" /></a>.</li>
          <li>Click <b>Create Credentials → OAuth client ID</b>.</li>
          <li>Application type: <b>Web application</b>.</li>
          <li>Under <b>Authorized JavaScript origins</b>, add: <code className="bg-white px-1 border rounded">{previewOrigin}</code> (and your production domain).</li>
          <li>Under <b>Authorized redirect URIs</b>, add the same URL(s).</li>
          <li>Copy the <b>Client ID</b> and <b>Client Secret</b> below and Save.</li>
        </ol>
      </div>

      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={form.enabled}
          onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
          data-testid="google-enabled-checkbox" />
        <span>Enable &quot;Sign in with Google&quot; on customer and vendor login</span>
      </label>

      <Field label="Client ID *" htmlFor="google_client_id" hint="From Google Cloud Console → OAuth client.">
        <Input id="google_client_id" value={form.client_id}
          onChange={(e) => setForm({ ...form, client_id: e.target.value })}
          placeholder="123456789-xxxx.apps.googleusercontent.com"
          data-testid="google-client-id-input" />
      </Field>

      <Field label="Client Secret" htmlFor="google_client_secret"
             hint="Optional for browser sign-in (we verify ID tokens). Save it here if you plan to add Google Drive / Calendar integrations later.">
        <SecretInput
          id="google_client_secret"
          type="password"
          autoComplete="new-password"
          placeholder={hints.client_secret ? '•••• keep unchanged' : 'GOCSPX-xxxxxxxxx'}
          value={form.client_secret}
          hint={hints.client_secret}
          onChange={(e) => setForm({ ...form, client_secret: e.target.value })}
          data-testid="google-client-secret-input"
        />
      </Field>

      <div className="flex justify-end pt-2 border-t">
        <Button type="submit" disabled={saving} className="bg-[#ee5e23] hover:bg-[#d54d18]" data-testid="google-save-btn">
          <Save className="w-4 h-4 mr-2" />{saving ? 'Saving...' : 'Save Google OAuth settings'}
        </Button>
      </div>
    </form>
  );
};

const FooterForm = () => {
  const { toast } = useToast();
  const [form, setForm] = useState({
    contact_phone: '', contact_email: '', contact_address: '',
    social_facebook: '', social_twitter: '', social_linkedin: '', social_instagram: '', social_youtube: '',
    tagline: 'Made in Telangana',
    copyright_text: '',
    column1_title: 'TelanganaMart', column2_title: 'Help', column3_title: 'For Vendors',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await adminApi.getSettings('footer');
        const v = r.value || {};
        setForm((f) => ({ ...f, ...v }));
      } catch (e) {
        toast({ title: 'Failed to load footer settings', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
      } finally { setLoading(false); }
    })();
    // Load once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async (e) => {
    e?.preventDefault?.();
    setSaving(true);
    try {
      await adminApi.saveSettings('footer', form);
      toast({ title: 'Footer settings saved', description: 'Reload the public site to see the changes.' });
    } catch (e) {
      toast({ title: 'Save failed', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    } finally { setSaving(false); }
  };

  if (loading) return <div className="text-gray-500">Loading footer settings...</div>;

  return (
    <form onSubmit={save} className="space-y-6" data-testid="site-settings-footer-form">
      <section>
        <h3 className="text-sm font-semibold mb-3 text-gray-800">Contact information</h3>
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Phone" htmlFor="ft_phone" hint="Shown beside the phone icon.">
            <Input id="ft_phone" value={form.contact_phone}
              onChange={(e) => setForm({ ...form, contact_phone: e.target.value })}
              placeholder="096-9696-9696"
              data-testid="footer-phone-input" />
          </Field>
          <Field label="Email" htmlFor="ft_email">
            <Input id="ft_email" type="email" value={form.contact_email}
              onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
              placeholder="customercare@telanganamart.com"
              data-testid="footer-email-input" />
          </Field>
          <Field label="Address" htmlFor="ft_address" hint="One-line address shown in the footer.">
            <Input id="ft_address" value={form.contact_address}
              onChange={(e) => setForm({ ...form, contact_address: e.target.value })}
              placeholder="HITEC City, Hyderabad"
              data-testid="footer-address-input" />
          </Field>
        </div>
      </section>

      <section>
        <h3 className="text-sm font-semibold mb-3 text-gray-800">Social media URLs</h3>
        <p className="text-xs text-gray-500 mb-3">Leave blank to hide that social icon from the footer.</p>
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Facebook URL" htmlFor="ft_fb"><Input id="ft_fb" value={form.social_facebook} onChange={(e) => setForm({ ...form, social_facebook: e.target.value })} placeholder="https://facebook.com/telanganamart" data-testid="footer-fb-input" /></Field>
          <Field label="Twitter / X URL" htmlFor="ft_tw"><Input id="ft_tw" value={form.social_twitter} onChange={(e) => setForm({ ...form, social_twitter: e.target.value })} placeholder="https://twitter.com/telanganamart" data-testid="footer-tw-input" /></Field>
          <Field label="LinkedIn URL" htmlFor="ft_li"><Input id="ft_li" value={form.social_linkedin} onChange={(e) => setForm({ ...form, social_linkedin: e.target.value })} placeholder="https://linkedin.com/company/telanganamart" data-testid="footer-li-input" /></Field>
          <Field label="Instagram URL" htmlFor="ft_ig"><Input id="ft_ig" value={form.social_instagram} onChange={(e) => setForm({ ...form, social_instagram: e.target.value })} placeholder="https://instagram.com/telanganamart" data-testid="footer-ig-input" /></Field>
          <Field label="YouTube URL" htmlFor="ft_yt"><Input id="ft_yt" value={form.social_youtube} onChange={(e) => setForm({ ...form, social_youtube: e.target.value })} placeholder="https://youtube.com/@telanganamart" data-testid="footer-yt-input" /></Field>
        </div>
      </section>

      <section>
        <h3 className="text-sm font-semibold mb-3 text-gray-800">Column titles</h3>
        <p className="text-xs text-gray-500 mb-3">Rename the three footer columns. Links inside are managed automatically (Pages tab + Vendor flows).</p>
        <div className="grid md:grid-cols-3 gap-4">
          <Field label="Column 1 title" htmlFor="ft_c1"><Input id="ft_c1" value={form.column1_title} onChange={(e) => setForm({ ...form, column1_title: e.target.value })} data-testid="footer-col1-input" /></Field>
          <Field label="Column 2 title" htmlFor="ft_c2"><Input id="ft_c2" value={form.column2_title} onChange={(e) => setForm({ ...form, column2_title: e.target.value })} data-testid="footer-col2-input" /></Field>
          <Field label="Column 3 title" htmlFor="ft_c3"><Input id="ft_c3" value={form.column3_title} onChange={(e) => setForm({ ...form, column3_title: e.target.value })} data-testid="footer-col3-input" /></Field>
        </div>
      </section>

      <section>
        <h3 className="text-sm font-semibold mb-3 text-gray-800">Bottom strip</h3>
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Copyright text" htmlFor="ft_copy" hint="Displayed at the bottom-left. Leave blank for the auto-generated default.">
            <Input id="ft_copy" value={form.copyright_text}
              onChange={(e) => setForm({ ...form, copyright_text: e.target.value })}
              placeholder="© 2026 TelanganaMart B2B Marketplace. All rights reserved."
              data-testid="footer-copyright-input" />
          </Field>
          <Field label="Tagline" htmlFor="ft_tagline" hint="Displayed at the bottom-right.">
            <Input id="ft_tagline" value={form.tagline}
              onChange={(e) => setForm({ ...form, tagline: e.target.value })}
              placeholder="Made in Telangana"
              data-testid="footer-tagline-input" />
          </Field>
        </div>
      </section>

      <div className="flex justify-end pt-2 border-t">
        <Button type="submit" disabled={saving} className="bg-[#ee5e23] hover:bg-[#d54d18]" data-testid="footer-save-btn">
          <Save className="w-4 h-4 mr-2" />{saving ? 'Saving...' : 'Save footer settings'}
        </Button>
      </div>

      <p className="text-xs text-gray-500 -mt-2">
        Need to add a custom footer link (like &quot;Careers&quot; or &quot;Shipping Policy&quot;)? Use the <b>Pages</b> tab — any published page with
        &quot;Show in footer&quot; checked appears automatically in the first column.
      </p>
    </form>
  );
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const absUrl = (u) => (!u ? '' : (u.startsWith('http') ? u : `${BACKEND_URL}${u}`));

const ImageUploadField = ({ label, value, onChange, prefix, testid, maxSizeKB, helperText }) => {
  const { toast } = useToast();
  const [uploading, setUploading] = useState(false);
  const inputId = `${testid}-file`;

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    // Client-side size guard. Sized in KB so OG / favicon uploads can enforce
    // tight limits (WhatsApp / Facebook OG scrapers refuse large images, so a
    // 500 KB cap keeps shares fast and reliable). Backend still re-validates.
    if (maxSizeKB && f.size > maxSizeKB * 1024) {
      const actualKB = Math.round(f.size / 1024);
      toast({
        title: `File too large — max ${maxSizeKB} KB`,
        description: `Your file is ${actualKB} KB. Compress with tinypng.com / squoosh.app and re-upload.`,
        variant: 'destructive',
      });
      e.target.value = '';
      return;
    }
    setUploading(true);
    try {
      const r = await adminApi.uploadImage(f, prefix);
      onChange(r.url);
      toast({ title: `${label} uploaded` });
    } catch (err) {
      toast({ title: 'Upload failed', description: err?.response?.data?.detail || err.message, variant: 'destructive' });
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  return (
    <div data-testid={`${testid}-wrap`}>
      <Label className="text-sm">{label}</Label>
      <div className="mt-1 flex items-start gap-4 flex-wrap">
        {value ? (
          <img
            src={absUrl(value)}
            alt={label}
            className="h-16 w-16 object-contain border border-gray-200 rounded bg-white p-1"
            data-testid={`${testid}-preview`}
          />
        ) : (
          <div className="h-16 w-16 border border-dashed border-gray-300 rounded flex items-center justify-center text-gray-400" data-testid={`${testid}-empty`}>
            <ImageIcon className="w-6 h-6" />
          </div>
        )}
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <input id={inputId} type="file" accept="image/*" onChange={onFile} className="hidden" data-testid={`${testid}-input`} />
            <label
              htmlFor={inputId}
              className={`inline-flex items-center gap-2 px-3 py-2 text-sm border border-gray-300 rounded-md cursor-pointer hover:bg-gray-50 ${uploading ? 'opacity-50 pointer-events-none' : ''}`}
              data-testid={`${testid}-trigger`}
            >
              <Upload className="w-4 h-4" /> {uploading ? 'Uploading…' : (value ? 'Replace' : 'Upload')}
            </label>
            {value && (
              <Button type="button" variant="outline" size="sm" onClick={() => onChange('')} data-testid={`${testid}-clear`}>
                Remove
              </Button>
            )}
          </div>
          <Input
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder="…or paste an image URL"
            className="text-xs"
            data-testid={`${testid}-url`}
          />
          {(helperText || maxSizeKB) && (
            <p className="text-[11px] text-gray-500 leading-snug" data-testid={`${testid}-helper`}>
              {helperText || `Max ${maxSizeKB} KB. PNG / JPG / WEBP. Use tinypng.com or squoosh.app to compress.`}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

const BrandingForm = () => {
  const { toast } = useToast();
  const [form, setForm] = useState({ site_name: '', logo_url: '', favicon_url: '', primary_color: '#ee5e23' });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await adminApi.getSettings('branding');
        const v = r.value || {};
        setForm({
          site_name: v.site_name || '',
          logo_url: v.logo_url || '',
          favicon_url: v.favicon_url || '',
          primary_color: v.primary_color || '#ee5e23',
        });
      } catch (e) {
        toast({ title: 'Failed to load branding', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
      } finally { setLoading(false); }
    })();
    // Load once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async (e) => {
    e?.preventDefault?.();
    setSaving(true);
    try {
      await adminApi.saveSettings('branding', form);
      toast({ title: 'Branding saved', description: 'Reload the site to see the new logo everywhere.' });
    } catch (err) {
      toast({ title: 'Save failed', description: err?.response?.data?.detail || err.message, variant: 'destructive' });
    } finally { setSaving(false); }
  };

  if (loading) return <div className="text-sm text-gray-500">Loading branding…</div>;

  return (
    <form onSubmit={save} className="space-y-6" data-testid="branding-form">
      <div>
        <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2"><ImageIcon className="w-5 h-5" /> Branding</h2>
        <p className="text-xs text-gray-500 mt-1">Site logo, favicon, name and primary color. Changes apply across the entire storefront immediately on next page load.</p>
      </div>

      <Field label="Site name" htmlFor="branding-site-name" hint="Used in browser tabs, emails and the header text logo fallback.">
        <Input id="branding-site-name" value={form.site_name} onChange={(e) => setForm({ ...form, site_name: e.target.value })} placeholder="TelanganaMart" data-testid="branding-site-name" />
      </Field>

      <ImageUploadField
        label="Main site logo"
        value={form.logo_url}
        onChange={(v) => setForm({ ...form, logo_url: v })}
        prefix="logo"
        testid="branding-logo"
      />

      <ImageUploadField
        label="Favicon (browser tab icon)"
        value={form.favicon_url}
        onChange={(v) => setForm({ ...form, favicon_url: v })}
        prefix="favicon"
        testid="branding-favicon"
      />

      <Field label="Primary color" htmlFor="branding-color" hint="Used for the browser theme color (mobile address bar tint).">
        <div className="flex items-center gap-3">
          <input
            type="color"
            id="branding-color"
            value={form.primary_color || '#ee5e23'}
            onChange={(e) => setForm({ ...form, primary_color: e.target.value })}
            className="h-10 w-16 border border-gray-300 rounded cursor-pointer"
            data-testid="branding-color-picker"
          />
          <Input
            value={form.primary_color || ''}
            onChange={(e) => setForm({ ...form, primary_color: e.target.value })}
            placeholder="#ee5e23"
            className="w-40"
            data-testid="branding-color-text"
          />
        </div>
      </Field>

      <div className="bg-blue-50 border border-blue-200 text-blue-900 text-xs rounded p-3" data-testid="branding-contact-hint">
        <strong>Tip:</strong> Customer-care phone, email, and address are managed in the <strong>Footer</strong> section (left sidebar). Those values are also displayed in the desktop top utility bar and on the Contact page.
      </div>

      <div className="flex justify-end pt-2 border-t">
        <Button type="submit" disabled={saving} className="bg-[#ee5e23] hover:bg-[#d54d18]" data-testid="branding-save-btn">
          <Save className="w-4 h-4 mr-2" />{saving ? 'Saving…' : 'Save branding'}
        </Button>
      </div>
    </form>
  );
};

const RazorpayForm = () => {
  const { toast } = useToast();
  const [form, setForm] = useState({
    mode: 'test',
    test_key_id: '', test_key_secret: '', test_webhook_secret: '',
    live_key_id: '', live_key_secret: '', live_webhook_secret: '',
    credits_inr_per_lead: 10,
    welcome_credits: 3,
    company_name: 'TelanganaMart', company_logo_url: '', currency: 'INR',
  });
  const [hints, setHints] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await adminApi.getSettings('razorpay');
        const v = r.value || {};
        setForm({
          mode: v.mode || 'test',
          test_key_id: v.test_key_id || '',
          test_key_secret: '',
          test_webhook_secret: '',
          live_key_id: v.live_key_id || '',
          live_key_secret: '',
          live_webhook_secret: '',
          credits_inr_per_lead: v.credits_inr_per_lead || 10,
          welcome_credits: v.welcome_credits ?? 3,
          company_name: v.company_name || 'TelanganaMart',
          company_logo_url: v.company_logo_url || '',
          currency: v.currency || 'INR',
        });
        setHints({
          test_key_secret: v.test_key_secret_hint || '',
          test_webhook_secret: v.test_webhook_secret_hint || '',
          live_key_secret: v.live_key_secret_hint || '',
          live_webhook_secret: v.live_webhook_secret_hint || '',
        });
      } catch (e) {
        toast({ title: 'Failed to load Razorpay settings', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
      } finally { setLoading(false); }
    })();
    // Load once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async (e) => {
    e?.preventDefault?.();
    setSaving(true);
    try {
      const payload = { ...form };
      // Don't send empty secrets — preserve previous value
      ['test_key_secret', 'test_webhook_secret', 'live_key_secret', 'live_webhook_secret'].forEach((k) => {
        if (!payload[k]) delete payload[k];
      });
      await adminApi.saveSettings('razorpay', payload);
      toast({ title: 'Razorpay settings saved' });
    } catch (err) {
      toast({ title: 'Save failed', description: err?.response?.data?.detail || err.message, variant: 'destructive' });
    } finally { setSaving(false); }
  };

  if (loading) return <div className="text-sm text-gray-500 p-6">Loading…</div>;
  const isTest = form.mode === 'test';

  return (
    <form onSubmit={save} className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="text-base font-semibold text-gray-900">Razorpay Payment Gateway</h3>
          <p className="text-xs text-gray-500">Configure keys for test &amp; live modes. Switch the active mode below.</p>
        </div>
        <div className="inline-flex bg-gray-100 rounded-md p-1 text-xs font-medium" data-testid="rzp-mode-toggle">
          <button type="button" onClick={() => setForm({ ...form, mode: 'test' })}
                  className={`px-3 py-1.5 rounded ${isTest ? 'bg-amber-500 text-white shadow' : 'text-gray-600'}`}
                  data-testid="rzp-mode-test">Test mode</button>
          <button type="button" onClick={() => setForm({ ...form, mode: 'live' })}
                  className={`px-3 py-1.5 rounded ${!isTest ? 'bg-emerald-600 text-white shadow' : 'text-gray-600'}`}
                  data-testid="rzp-mode-live">Live mode</button>
        </div>
      </div>

      <div className={`p-4 rounded-md border ${isTest ? 'bg-amber-50 border-amber-200' : 'bg-gray-50 border-gray-200'}`}>
        <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
          <CreditCard className="w-4 h-4" /> Test credentials
          {isTest && <span className="text-[10px] uppercase tracking-wider bg-amber-200 text-amber-900 px-1.5 py-0.5 rounded">active</span>}
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Test Key ID" htmlFor="rzp-test-id" hint="From Razorpay Dashboard → Settings → API Keys (starts with rzp_test_)">
            <Input id="rzp-test-id" value={form.test_key_id} onChange={(e) => setForm({ ...form, test_key_id: e.target.value })} placeholder="rzp_test_xxxxxxxx" data-testid="rzp-test-id" />
          </Field>
          <Field label="Test Key Secret" htmlFor="rzp-test-secret" hint={hints.test_key_secret ? `Saved: ${hints.test_key_secret}` : 'Leave blank to keep existing'}>
            <Input id="rzp-test-secret" type="password" value={form.test_key_secret} onChange={(e) => setForm({ ...form, test_key_secret: e.target.value })} placeholder={hints.test_key_secret ? '••••••••' : 'Test key secret'} data-testid="rzp-test-secret" />
          </Field>
          <Field label="Test Webhook Secret (optional)" htmlFor="rzp-test-webhook" hint={hints.test_webhook_secret ? `Saved: ${hints.test_webhook_secret}` : 'Razorpay does NOT auto-provide this. Create a webhook at Razorpay Dashboard → Webhooks (later), pick your own secret there, then paste it here. Leave blank for now — payments still work without it.'} className="md:col-span-2">
            <Input id="rzp-test-webhook" type="password" value={form.test_webhook_secret} onChange={(e) => setForm({ ...form, test_webhook_secret: e.target.value })} placeholder={hints.test_webhook_secret ? '••••••••' : 'Leave blank for now (whsec_… later)'} data-testid="rzp-test-webhook" />
          </Field>
        </div>
      </div>

      <div className={`p-4 rounded-md border ${!isTest ? 'bg-emerald-50 border-emerald-200' : 'bg-gray-50 border-gray-200'}`}>
        <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
          <CreditCard className="w-4 h-4" /> Live credentials
          {!isTest && <span className="text-[10px] uppercase tracking-wider bg-emerald-200 text-emerald-900 px-1.5 py-0.5 rounded">active</span>}
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Live Key ID" htmlFor="rzp-live-id" hint="Production key (starts with rzp_live_)">
            <Input id="rzp-live-id" value={form.live_key_id} onChange={(e) => setForm({ ...form, live_key_id: e.target.value })} placeholder="rzp_live_xxxxxxxx" data-testid="rzp-live-id" />
          </Field>
          <Field label="Live Key Secret" htmlFor="rzp-live-secret" hint={hints.live_key_secret ? `Saved: ${hints.live_key_secret}` : 'Leave blank to keep existing'}>
            <Input id="rzp-live-secret" type="password" value={form.live_key_secret} onChange={(e) => setForm({ ...form, live_key_secret: e.target.value })} placeholder={hints.live_key_secret ? '••••••••' : 'Live key secret'} data-testid="rzp-live-secret" />
          </Field>
          <Field label="Live Webhook Secret (optional)" htmlFor="rzp-live-webhook" hint={hints.live_webhook_secret ? `Saved: ${hints.live_webhook_secret}` : 'Pick your own secret when creating the production webhook in Razorpay Dashboard.'} className="md:col-span-2">
            <Input id="rzp-live-webhook" type="password" value={form.live_webhook_secret} onChange={(e) => setForm({ ...form, live_webhook_secret: e.target.value })} placeholder={hints.live_webhook_secret ? '••••••••' : 'Leave blank for now'} data-testid="rzp-live-webhook" />
          </Field>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Field label="₹ per lead (top-up)" htmlFor="rzp-credit-rate" hint="Vendor pays this many ₹ per additional lead.">
          <Input id="rzp-credit-rate" type="number" min="1" value={form.credits_inr_per_lead} onChange={(e) => setForm({ ...form, credits_inr_per_lead: Number(e.target.value) || 0 })} data-testid="rzp-credit-rate" />
        </Field>
        <Field label="Free welcome credits" htmlFor="rzp-welcome-credits" hint="Lead credits gifted to every new vendor on signup. Set 0 to disable.">
          <Input id="rzp-welcome-credits" type="number" min="0" value={form.welcome_credits} onChange={(e) => setForm({ ...form, welcome_credits: Math.max(0, Number(e.target.value) || 0) })} data-testid="rzp-welcome-credits" />
        </Field>
        <Field label="Company name (shown on checkout)" htmlFor="rzp-company">
          <Input id="rzp-company" value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })} data-testid="rzp-company-name" />
        </Field>
        <Field label="Company logo URL (checkout)" htmlFor="rzp-logo">
          <Input id="rzp-logo" value={form.company_logo_url} onChange={(e) => setForm({ ...form, company_logo_url: e.target.value })} placeholder="https://..." data-testid="rzp-company-logo" />
        </Field>
      </div>

      <div className="bg-blue-50 border border-blue-200 text-blue-900 text-xs rounded p-3 space-y-1" data-testid="rzp-webhook-hint">
        <div><strong>One-time payments work without a webhook</strong> — vendors can buy plans &amp; lead credits using just the Key ID + Key Secret you&apos;ve configured above.</div>
        <div className="pt-1 border-t border-blue-200">For automatic recurring subscription renewals and payment-failed notifications, set up a webhook later:</div>
        <ol className="list-decimal ml-5 space-y-0.5">
          <li>Go to <a href="https://dashboard.razorpay.com/app/webhooks" target="_blank" rel="noopener noreferrer" className="underline">Razorpay Dashboard → Webhooks</a></li>
          <li>URL: <code className="bg-white px-1.5 py-0.5 rounded font-mono">{process.env.REACT_APP_BACKEND_URL}/api/webhooks/razorpay</code></li>
          <li>Pick a <em>secret string</em> (you invent it) and subscribe to: <code>payment.captured</code>, <code>payment.failed</code>, <code>subscription.charged</code>, <code>subscription.cancelled</code>, <code>refund.processed</code></li>
          <li>Paste that secret into the Webhook Secret field above and save again.</li>
        </ol>
      </div>

      <div className="flex justify-end pt-2">
        <Button type="submit" disabled={saving} className="bg-[#ee5e23] hover:bg-[#d24e15] text-white" data-testid="rzp-save-btn">
          <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving…' : 'Save Razorpay settings'}
        </Button>
      </div>
    </form>
  );
};



const SeoForm = () => {
  const { toast } = useToast();
  const [form, setForm] = useState({
    home_title: '', home_description: '', home_keywords: '',
    og_image: '', og_title: '', og_description: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await adminApi.getSettings('seo');
        const v = r.value || {};
        setForm({
          home_title: v.home_title || '',
          home_description: v.home_description || '',
          home_keywords: v.home_keywords || '',
          og_image: v.og_image || '',
          og_title: v.og_title || '',
          og_description: v.og_description || '',
        });
      } catch (e) {
        toast({ title: 'Failed to load SEO', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
      } finally { setLoading(false); }
    })();
    // Load once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async (e) => {
    e?.preventDefault?.();
    setSaving(true);
    try {
      await adminApi.saveSettings('seo', form);
      toast({ title: 'SEO settings saved' });
    } catch (err) {
      toast({ title: 'Save failed', description: err?.response?.data?.detail || err.message, variant: 'destructive' });
    } finally { setSaving(false); }
  };

  if (loading) return <div className="text-sm text-gray-500">Loading SEO…</div>;

  return (
    <form onSubmit={save} className="space-y-6" data-testid="seo-form">
      <div>
        <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2"><SearchIcon className="w-5 h-5" /> Home page SEO &amp; Meta tags</h2>
        <p className="text-xs text-gray-500 mt-1">These tags drive Google search snippets and social previews. The home page uses them directly; inner pages fall back to these values.</p>
      </div>

      <Field label="Page title" htmlFor="seo-title" hint="Shown in browser tab and Google result heading. ~60 characters recommended.">
        <Input id="seo-title" maxLength={120} value={form.home_title} onChange={(e) => setForm({ ...form, home_title: e.target.value })} placeholder="TelanganaMart - Telangana's Largest B2B Marketplace" data-testid="seo-home-title" />
        <div className="text-xs text-gray-400 mt-1">{form.home_title.length}/60</div>
      </Field>

      <Field label="Meta description" htmlFor="seo-desc" hint="Snippet shown below the title in Google. ~160 characters recommended.">
        <textarea
          id="seo-desc"
          rows={3}
          maxLength={320}
          value={form.home_description}
          onChange={(e) => setForm({ ...form, home_description: e.target.value })}
          placeholder="Telangana's largest B2B marketplace. Verified vendors, instant quotes, OTP-verified leads."
          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
          data-testid="seo-home-description"
        />
        <div className="text-xs text-gray-400 mt-1">{form.home_description.length}/160</div>
      </Field>

      <Field label="Meta keywords" htmlFor="seo-keywords" hint="Comma separated. Helps internal search relevance even though Google ignores them.">
        <Input id="seo-keywords" value={form.home_keywords} onChange={(e) => setForm({ ...form, home_keywords: e.target.value })} placeholder="b2b marketplace, telangana suppliers, hyderabad wholesale, indiamart alternative" data-testid="seo-home-keywords" />
      </Field>

      <div className="pt-4 border-t">
        <h3 className="text-base font-semibold text-gray-900 mb-1">Social sharing (Open Graph &amp; Twitter)</h3>
        <p className="text-xs text-gray-500 mb-4">Optional. Leave blank to reuse the meta title / description above.</p>
      </div>

      <Field label="OG title" htmlFor="seo-og-title">
        <Input id="seo-og-title" value={form.og_title} onChange={(e) => setForm({ ...form, og_title: e.target.value })} placeholder="Same as page title" data-testid="seo-og-title" />
      </Field>

      <Field label="OG description" htmlFor="seo-og-desc">
        <Input id="seo-og-desc" value={form.og_description} onChange={(e) => setForm({ ...form, og_description: e.target.value })} placeholder="Same as meta description" data-testid="seo-og-description" />
      </Field>

      <ImageUploadField
        label="OG image (used for WhatsApp / Facebook / LinkedIn previews)"
        value={form.og_image}
        onChange={(v) => setForm({ ...form, og_image: v })}
        prefix="og"
        testid="seo-og-image"
        maxSizeKB={500}
        helperText="Recommended 1200×630 px. Max 500 KB (WhatsApp / Facebook scrapers reject larger files). Compress with tinypng.com or squoosh.app."
      />

      <div className="flex justify-end pt-2 border-t">
        <Button type="submit" disabled={saving} className="bg-[#ee5e23] hover:bg-[#d54d18]" data-testid="seo-save-btn">
          <Save className="w-4 h-4 mr-2" />{saving ? 'Saving…' : 'Save SEO settings'}
        </Button>
      </div>
    </form>
  );
};

const ActiveProviderBanner = () => {
  const { toast } = useToast();
  const [provider, setProvider] = useState('msg91');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await adminApi.getSettings('sms');
        setProvider((r.value?.active_provider || 'msg91').toLowerCase());
      } catch (err) { console.warn('SiteSettings: failed to read active SMS provider, using msg91', err); }
      finally { setLoading(false); }
    })();
    // Load once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const change = async (newProv) => {
    if (newProv === provider) return;
    setSaving(true);
    try {
      await adminApi.saveSettings('sms', { active_provider: newProv });
      setProvider(newProv);
      toast({ title: `Active SMS gateway → ${newProv === 'msg91' ? 'MSG91' : 'SMSLogin'}` });
    } catch (e) {
      toast({ title: 'Failed to switch provider', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    } finally { setSaving(false); }
  };

  if (loading) return null;

  return (
    <div className="mb-5 p-4 bg-gradient-to-r from-orange-50 to-amber-50 border border-orange-200 rounded-lg flex items-center gap-3 flex-wrap" data-testid="active-sms-provider-banner">
      <MessageCircle className="w-5 h-5 text-[#ee5e23]" />
      <div className="text-sm">
        <div className="font-semibold text-gray-900">Active SMS gateway</div>
        <div className="text-xs text-gray-600">All OTP &amp; transactional SMS are routed through the selected gateway.</div>
      </div>
      <select
        value={provider}
        onChange={(e) => change(e.target.value)}
        disabled={saving}
        className="ml-auto border-2 border-[#ee5e23] rounded-md px-3 py-2 text-sm font-semibold bg-white"
        data-testid="active-sms-provider-select"
      >
        <option value="msg91">MSG91</option>
        <option value="smslogin">SMSLogin (Genfon)</option>
      </select>
    </div>
  );
};

const NavForm = () => {
  const { toast } = useToast();
  const [form, setForm] = useState({ show_mega_menu: true });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await adminApi.getSettings('nav');
        const cfg = r?.value || {};
        setForm({
          show_mega_menu: cfg.show_mega_menu !== false,
        });
      } catch (e) {
        toast({ title: 'Failed to load navigation settings', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
      } finally { setLoading(false); }
    })();
  }, [toast]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await adminApi.saveSettings('nav', form);
      toast({ title: 'Navigation settings saved', description: 'Refresh the public site to see the change live.' });
    } catch (err) {
      toast({ title: 'Save failed', description: err?.response?.data?.detail || err.message, variant: 'destructive' });
    } finally { setSaving(false); }
  };

  if (loading) return <div className="text-gray-500">Loading navigation settings...</div>;

  return (
    <form onSubmit={save} className="space-y-6" data-testid="site-settings-nav-form">
      <div>
        <h3 className="text-sm font-bold text-gray-900 mb-1">Mega-menu visibility</h3>
        <p className="text-xs text-gray-500 mb-3">Toggle the full-width category mega-menu that appears below the main header on every public page.</p>
        <label className="flex items-start gap-3 p-3 rounded-md border border-gray-200 bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors">
          <input
            type="checkbox"
            checked={!!form.show_mega_menu}
            onChange={(e) => setForm({ ...form, show_mega_menu: e.target.checked })}
            className="mt-0.5 w-4 h-4 accent-[#ee5e23]"
            data-testid="nav-show-mega-menu-toggle"
          />
          <div>
            <div className="text-sm font-semibold text-gray-900">Show mega-menu on the public site</div>
            <div className="text-xs text-gray-600">
              When enabled, the desktop strip listing all main categories (Industrial Supplies, Building &amp; Real Estate, etc.) is rendered below the header, and the same categories appear in the mobile drawer. Disable to fully hide the mega-menu — categories can still be reached via the homepage grid, search box, and direct URLs.
            </div>
          </div>
        </label>
      </div>
      <div>
        <Button type="submit" disabled={saving} className="bg-[#ee5e23] hover:bg-[#d54d18]" data-testid="nav-save-btn">
          <Save className="w-4 h-4 mr-2" />{saving ? 'Saving...' : 'Save navigation settings'}
        </Button>
      </div>
    </form>
  );
};


const PwaForm = () => {
  const { toast } = useToast();
  const [form, setForm] = useState({
    app_name: '', short_name: '',
    theme_color: '#ee5e23', background_color: '#ffffff',
    icon_192_url: '', icon_512_url: '', apple_touch_icon_url: '',
    display: 'standalone', orientation: 'portrait',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await adminApi.getSettings('pwa');
        const cfg = r?.value || {};
        setForm((prev) => ({ ...prev, ...cfg }));
      } catch (e) {
        toast({ title: 'Failed to load PWA settings', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
      } finally { setLoading(false); }
    })();
  }, [toast]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await adminApi.saveSettings('pwa', form);
      toast({ title: 'PWA settings saved', description: 'Reload the public site to refresh the manifest.' });
    } catch (err) {
      toast({ title: 'Save failed', description: err?.response?.data?.detail || err.message, variant: 'destructive' });
    } finally { setSaving(false); }
  };

  if (loading) return <div className="text-gray-500">Loading PWA settings…</div>;

  return (
    <form onSubmit={save} className="space-y-6" data-testid="site-settings-pwa-form">
      <p className="text-xs text-gray-500 -mt-2">
        These values power the browser&apos;s &ldquo;Add to Home Screen&rdquo; / install prompt. They&apos;re served at
        <code className="mx-1 px-1 py-0.5 bg-gray-100 rounded">/api/manifest.json</code> and read live by every visitor.
      </p>

      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="App name (full)" htmlFor="pwa-app-name">
          <Input id="pwa-app-name" maxLength={45} value={form.app_name} onChange={(e) => setForm({ ...form, app_name: e.target.value })} placeholder="TelanganaMart" data-testid="pwa-app-name" />
        </Field>
        <Field label="Short name (≤ 12 chars, used on home-screen)" htmlFor="pwa-short-name">
          <Input id="pwa-short-name" maxLength={12} value={form.short_name} onChange={(e) => setForm({ ...form, short_name: e.target.value })} placeholder="TGMart" data-testid="pwa-short-name" />
        </Field>
        <Field label="Theme color (status bar)" htmlFor="pwa-theme">
          <Input id="pwa-theme" type="color" value={form.theme_color || '#ee5e23'} onChange={(e) => setForm({ ...form, theme_color: e.target.value })} data-testid="pwa-theme-color" />
        </Field>
        <Field label="Background color (splash screen)" htmlFor="pwa-bg">
          <Input id="pwa-bg" type="color" value={form.background_color || '#ffffff'} onChange={(e) => setForm({ ...form, background_color: e.target.value })} data-testid="pwa-bg-color" />
        </Field>
      </div>

      <ImageUploadField
        label="App icon — 192 × 192 (required)"
        value={form.icon_192_url}
        onChange={(v) => setForm({ ...form, icon_192_url: v })}
        prefix="pwa-192"
        testid="pwa-icon-192"
      />
      <ImageUploadField
        label="App icon — 512 × 512 (required for splash screen)"
        value={form.icon_512_url}
        onChange={(v) => setForm({ ...form, icon_512_url: v })}
        prefix="pwa-512"
        testid="pwa-icon-512"
      />
      <ImageUploadField
        label="Apple touch icon — 180 × 180 (iOS home-screen icon)"
        value={form.apple_touch_icon_url}
        onChange={(v) => setForm({ ...form, apple_touch_icon_url: v })}
        prefix="pwa-apple"
        testid="pwa-apple-icon"
      />

      <div className="flex justify-end pt-2 border-t">
        <Button type="submit" disabled={saving} className="bg-[#ee5e23] hover:bg-[#d54d18]" data-testid="pwa-save-btn">
          <Save className="w-4 h-4 mr-2" />{saving ? 'Saving…' : 'Save PWA settings'}
        </Button>
      </div>
    </form>
  );
};


const InboxForm = () => {
  const { toast } = useToast();
  const [mode, setMode] = useState('instant');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await adminApi.getSettings('inbox');
        setMode((r?.value?.notification_mode || 'instant').toLowerCase());
      } catch (e) {
        // First load — section may not exist yet, default to instant.
      } finally { setLoading(false); }
    })();
  }, []);

  const save = async (e) => {
    e?.preventDefault?.();
    setSaving(true);
    try {
      await adminApi.saveSettings('inbox', { notification_mode: mode });
      toast({ title: 'Inbox notification mode saved', description: mode === 'digest' ? '2x daily digest emails will be sent to vendors.' : 'Every new buyer message will email the vendor instantly.' });
    } catch (err) {
      toast({ title: 'Save failed', description: err?.response?.data?.detail || err.message, variant: 'destructive' });
    } finally { setSaving(false); }
  };

  const runNow = async () => {
    setRunning(true);
    try {
      const r = await adminApi.runInboxDigest();
      toast({ title: 'Digest dispatched', description: `Vendors notified: ${r.vendors || 0} · Messages batched: ${r.messages || 0}` });
    } catch (err) {
      toast({ title: 'Digest failed', description: err.message, variant: 'destructive' });
    } finally { setRunning(false); }
  };

  if (loading) return <div className="text-gray-500">Loading inbox settings…</div>;

  return (
    <form onSubmit={save} className="space-y-6" data-testid="site-settings-inbox-form">
      <p className="text-xs text-gray-500 -mt-2">
        Controls how vendors are emailed when buyers send new messages to their <code className="mx-1 px-1 py-0.5 bg-gray-100 rounded">/vendor/messages</code> inbox. Buyer-side notifications (quotations and revisions from vendors) are always sent instantly — only the vendor side is batched.
      </p>

      <div className="space-y-2">
        <label className={`flex items-start gap-3 p-3 border rounded-md cursor-pointer transition ${mode === 'instant' ? 'border-[#ee5e23] bg-orange-50/40' : 'border-gray-200 hover:bg-gray-50'}`} data-testid="inbox-mode-instant">
          <input type="radio" name="mode" value="instant" checked={mode === 'instant'} onChange={() => setMode('instant')} className="mt-0.5" />
          <div>
            <div className="text-sm font-medium">Instant</div>
            <div className="text-xs text-gray-500 mt-0.5">One email per new buyer message. Best for low-volume vendors.</div>
          </div>
        </label>
        <label className={`flex items-start gap-3 p-3 border rounded-md cursor-pointer transition ${mode === 'digest' ? 'border-[#ee5e23] bg-orange-50/40' : 'border-gray-200 hover:bg-gray-50'}`} data-testid="inbox-mode-digest">
          <input type="radio" name="mode" value="digest" checked={mode === 'digest'} onChange={() => setMode('digest')} className="mt-0.5" />
          <div>
            <div className="text-sm font-medium">Digest (every 12h)</div>
            <div className="text-xs text-gray-500 mt-0.5">Batches all pending buyer messages into a single email per vendor, sent twice a day. Better for high-volume sellers and lowers SMTP cost.</div>
          </div>
        </label>
      </div>

      <div className="flex items-center gap-2">
        <Button type="submit" disabled={saving} className="bg-[#ee5e23] hover:bg-[#d24e15] text-white" data-testid="inbox-save-btn">{saving ? 'Saving…' : 'Save'}</Button>
        <Button type="button" onClick={runNow} disabled={running} variant="outline" data-testid="inbox-run-digest-btn">{running ? 'Running…' : 'Run digest now'}</Button>
      </div>
    </form>
  );
};

/* ---------------- AI (Gemini) ---------------- */
const AI_DEFAULT_FORM = {
  model: 'gemini-3-flash-preview',
  enable_chatbot: true,
  enable_product_desc: true,
  enable_cms: true,
  enable_matcher: true,
  enable_ai_search: true,
  chatbot_system_prompt: '',
  product_desc_system_prompt: '',
  cms_system_prompt: '',
  matcher_system_prompt: '',
  ai_search_system_prompt: '',
};

const AiForm = () => {
  const { toast } = useToast();
  const [form, setForm] = useState(AI_DEFAULT_FORM);
  const [defaults, setDefaults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [{ value } = {}, d] = await Promise.all([
          adminApi.getSettings('ai').catch(() => ({})),
          adminApi.aiGetDefaults().catch(() => null),
        ]);
        if (d) {
          setDefaults(d);
          setForm((f) => ({
            ...f,
            model: value?.model || d.default_model,
            chatbot_system_prompt: value?.chatbot_system_prompt ?? d.default_prompts?.chatbot_system_prompt ?? '',
            product_desc_system_prompt: value?.product_desc_system_prompt ?? d.default_prompts?.product_desc_system_prompt ?? '',
            cms_system_prompt: value?.cms_system_prompt ?? d.default_prompts?.cms_system_prompt ?? '',
            matcher_system_prompt: value?.matcher_system_prompt ?? d.default_prompts?.matcher_system_prompt ?? '',
            ai_search_system_prompt: value?.ai_search_system_prompt ?? d.default_prompts?.ai_search_system_prompt ?? '',
            enable_chatbot: value?.enable_chatbot ?? true,
            enable_product_desc: value?.enable_product_desc ?? true,
            enable_cms: value?.enable_cms ?? true,
            enable_matcher: value?.enable_matcher ?? true,
            enable_ai_search: value?.enable_ai_search ?? true,
          }));
        } else if (value) {
          setForm((f) => ({ ...f, ...value }));
        }
      } finally { setLoading(false); }
    })();
  }, []);

  const save = async (e) => {
    e?.preventDefault?.();
    setSaving(true);
    try {
      await adminApi.saveSettings('ai', form);
      toast({ title: 'AI settings saved' });
    } catch (err) {
      toast({ title: 'Save failed', description: err?.response?.data?.detail || err.message, variant: 'destructive' });
    } finally { setSaving(false); }
  };

  const runTest = async () => {
    setTesting(true); setTestResult(null);
    try {
      const r = await adminApi.aiTest('Reply with the single word OK.');
      setTestResult(r);
    } catch (err) {
      setTestResult({ ok: false, error: err?.response?.data?.detail || err.message });
    } finally { setTesting(false); }
  };

  const resetPrompt = (key) => {
    if (!defaults?.default_prompts?.[key]) return;
    setForm((f) => ({ ...f, [key]: defaults.default_prompts[key] }));
  };

  if (loading) return <div className="text-gray-500">Loading AI settings…</div>;

  return (
    <form onSubmit={save} className="space-y-5" data-testid="site-settings-ai-form">
      <div className="text-sm text-gray-600">
        Gemini chat models power the buyer support chatbot, vendor product description writer,
        admin CMS assistant and requirement matcher. All requests use the shared <code className="bg-gray-100 px-1 rounded">EMERGENT_LLM_KEY</code>.
      </div>

      <div>
        <Label className="text-xs">Active model</Label>
        <select
          value={form.model}
          onChange={(e) => setForm({ ...form, model: e.target.value })}
          className="mt-1 w-full max-w-sm border border-gray-300 rounded-md px-3 py-2 text-sm bg-white"
          data-testid="ai-model-select"
        >
          {(defaults?.available_models || ['gemini-3-flash-preview']).map((m) => (
            <option key={m} value={m}>{m}</option>
          ))}
        </select>
        <p className="text-[11px] text-gray-500 mt-1">Flash models are fastest and cheapest. Pro models produce more thorough answers but take longer.</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          ['enable_chatbot', 'Buyer chatbot'],
          ['enable_product_desc', 'Product description writer'],
          ['enable_cms', 'CMS assistant'],
          ['enable_matcher', 'Requirement matcher'],
          ['enable_ai_search', 'Homepage AI search'],
        ].map(([k, label]) => (
          <label key={k} className="flex items-center gap-2 border border-gray-200 rounded-md p-2 cursor-pointer hover:bg-gray-50 text-xs">
            <input
              type="checkbox"
              checked={!!form[k]}
              onChange={(e) => setForm({ ...form, [k]: e.target.checked })}
              data-testid={`ai-toggle-${k}`}
            />
            <span>{label}</span>
          </label>
        ))}
      </div>

      {[
        ['chatbot_system_prompt', 'Chatbot system prompt', 'Guides the public support assistant.'],
        ['product_desc_system_prompt', 'Product description prompt', 'Used by the vendor "Generate with AI" button.'],
        ['cms_system_prompt', 'CMS assistant prompt', 'Used by "Generate" and "Rewrite" on Pages.'],
        ['matcher_system_prompt', 'Requirement matcher prompt', 'Must return valid JSON — do not remove the JSON instruction.'],
        ['ai_search_system_prompt', 'Homepage AI search prompt', 'Parses NL queries into JSON with keywords/city/intent — must return JSON.'],
      ].map(([k, label, hint]) => (
        <div key={k}>
          <div className="flex items-center justify-between mb-1">
            <Label className="text-xs">{label}</Label>
            <button type="button" onClick={() => resetPrompt(k)}
              className="text-[11px] text-gray-500 hover:text-[#ee5e23] inline-flex items-center gap-1"
              data-testid={`ai-reset-${k}`}>
              <RefreshCw className="w-3 h-3" /> Reset to default
            </button>
          </div>
          <Textarea
            value={form[k] || ''}
            onChange={(e) => setForm({ ...form, [k]: e.target.value })}
            rows={4}
            className="font-mono text-xs"
            data-testid={`ai-input-${k}`}
          />
          <p className="text-[11px] text-gray-500 mt-1">{hint}</p>
        </div>
      ))}

      <div className="flex items-center gap-2 pt-2 border-t">
        <Button type="submit" disabled={saving} className="bg-[#ee5e23] hover:bg-[#d24e15] text-white" data-testid="ai-save-btn">
          <Save className="w-4 h-4 mr-1" /> {saving ? 'Saving…' : 'Save AI settings'}
        </Button>
        <Button type="button" variant="outline" onClick={runTest} disabled={testing} data-testid="ai-test-btn">
          <Send className="w-4 h-4 mr-1" /> {testing ? 'Testing…' : 'Test model'}
        </Button>
        {testResult && (
          <span className={`text-xs ml-2 ${testResult.ok ? 'text-green-700' : 'text-red-600'}`} data-testid="ai-test-result">
            {testResult.ok ? `✓ ${testResult.model}: “${testResult.reply}”` : `✗ ${testResult.error || 'Test failed'}`}
          </span>
        )}
      </div>
    </form>
  );
};


const StorageForm = () => {
  const { toast } = useToast();
  const [form, setForm] = useState({
    provider: 'emergent',
    aws_access_key_id: '', aws_access_key_id_hint: '',
    aws_secret_access_key: '', aws_secret_access_key_hint: '',
    aws_region: '', aws_bucket: '',
    aws_endpoint_url: '', aws_public_url_prefix: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await adminApi.getSettings('storage');
        setForm((f) => ({ ...f, ...(r?.value || {}) }));
      } catch (e) {
        // empty bucket on first load — defaults stay
      } finally { setLoading(false); }
    })();
  }, []);

  const save = async (e) => {
    e?.preventDefault?.();
    setSaving(true);
    try {
      // Strip the *_hint fields — they're read-only display values, not config.
      const { aws_access_key_id_hint, aws_secret_access_key_hint, ...payload } = form;
      const r = await adminApi.saveSettings('storage', payload);
      setForm((f) => ({ ...f, ...(r?.value || {}) }));
      toast({ title: 'Storage settings saved', description: `Active provider: ${payload.provider}` });
    } catch (err) {
      toast({ title: 'Save failed', description: err?.response?.data?.detail || err.message, variant: 'destructive' });
    } finally { setSaving(false); }
  };

  const testS3 = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const r = await adminApi.testStorage();
      setTestResult(r);
      toast({
        title: r.ok ? 'S3 connection OK' : 'S3 test failed',
        description: r.detail,
        variant: r.ok ? 'default' : 'destructive',
      });
    } catch (err) {
      setTestResult({ ok: false, detail: err?.response?.data?.detail || err.message });
    } finally { setTesting(false); }
  };

  if (loading) return <div className="text-gray-500">Loading storage settings…</div>;

  const isS3 = form.provider === 's3';

  return (
    <form onSubmit={save} className="space-y-6" data-testid="site-settings-storage-form">
      <p className="text-xs text-gray-500 -mt-2">
        Where admin- and vendor-uploaded files (logos, product images, certificates, OG images) are persisted.
        Switching to <b>AWS S3</b> requires the four credential fields below to be filled. Files uploaded
        under the previous provider remain accessible — only new uploads go to the new bucket.
      </p>

      <div className="space-y-2">
        <Label className="text-sm">Active provider</Label>
        <div className="grid sm:grid-cols-2 gap-3">
          <label
            className={`flex items-start gap-3 p-3 border rounded-md cursor-pointer transition ${form.provider === 'emergent' ? 'border-[#ee5e23] bg-orange-50/40' : 'border-gray-200 hover:bg-gray-50'}`}
            data-testid="storage-provider-emergent"
          >
            <input
              type="radio" name="provider" value="emergent"
              checked={form.provider === 'emergent'}
              onChange={() => setForm({ ...form, provider: 'emergent' })}
              className="mt-0.5"
            />
            <div>
              <div className="text-sm font-medium">Emergent Managed Storage</div>
              <div className="text-xs text-gray-500 mt-0.5">Zero-config. Uses the platform-managed bucket. Files survive every redeploy.</div>
            </div>
          </label>
          <label
            className={`flex items-start gap-3 p-3 border rounded-md cursor-pointer transition ${form.provider === 's3' ? 'border-[#ee5e23] bg-orange-50/40' : 'border-gray-200 hover:bg-gray-50'}`}
            data-testid="storage-provider-s3"
          >
            <input
              type="radio" name="provider" value="s3"
              checked={form.provider === 's3'}
              onChange={() => setForm({ ...form, provider: 's3' })}
              className="mt-0.5"
            />
            <div>
              <div className="text-sm font-medium">AWS S3 (or S3-compatible)</div>
              <div className="text-xs text-gray-500 mt-0.5">Your own bucket. Works with Cloudflare R2, Backblaze B2 etc. Fill the keys below.</div>
            </div>
          </label>
        </div>
      </div>

      <div className={`grid sm:grid-cols-2 gap-4 ${isS3 ? '' : 'opacity-70'}`}>
        <Field label="AWS Access Key ID" htmlFor="storage-akid" hint="From IAM → Users → Security credentials. Needs s3:PutObject + s3:GetObject + s3:DeleteObject on the bucket.">
          <SecretInput
            id="storage-akid"
            type="password"
            value={form.aws_access_key_id}
            onChange={(e) => setForm({ ...form, aws_access_key_id: e.target.value })}
            placeholder="AKIA…"
            hint={form.aws_access_key_id_hint}
            data-testid="storage-aws-access-key"
          />
        </Field>
        <Field label="AWS Secret Access Key" htmlFor="storage-sak" hint="Shown once in the AWS console — copy carefully.">
          <SecretInput
            id="storage-sak"
            type="password"
            value={form.aws_secret_access_key}
            onChange={(e) => setForm({ ...form, aws_secret_access_key: e.target.value })}
            placeholder="••••••••"
            hint={form.aws_secret_access_key_hint}
            data-testid="storage-aws-secret-key"
          />
        </Field>
        <Field label="AWS Region" htmlFor="storage-region" hint="e.g. ap-south-1 (Mumbai), us-east-1 (N. Virginia)">
          <Input
            id="storage-region"
            value={form.aws_region}
            onChange={(e) => setForm({ ...form, aws_region: e.target.value })}
            placeholder="ap-south-1"
            data-testid="storage-aws-region"
          />
        </Field>
        <Field label="Bucket name" htmlFor="storage-bucket" hint="Bucket must already exist. Recommended: enable Block Public Access OFF only if not using CloudFront.">
          <Input
            id="storage-bucket"
            value={form.aws_bucket}
            onChange={(e) => setForm({ ...form, aws_bucket: e.target.value })}
            placeholder="my-marketplace-uploads"
            data-testid="storage-aws-bucket"
          />
        </Field>
        <Field label="Endpoint URL (optional)" htmlFor="storage-endpoint" hint="Only for S3-compatible providers (Cloudflare R2, Backblaze B2, MinIO). Leave blank for AWS S3.">
          <Input
            id="storage-endpoint"
            value={form.aws_endpoint_url}
            onChange={(e) => setForm({ ...form, aws_endpoint_url: e.target.value })}
            placeholder="https://<accountid>.r2.cloudflarestorage.com"
            data-testid="storage-aws-endpoint"
          />
        </Field>
        <Field label="Public URL prefix / CDN (optional)" htmlFor="storage-cdn" hint="If your bucket is fronted by CloudFront or has a public URL, paste it here so browsers fetch images directly (faster). Otherwise files are streamed through /api/files/...">
          <Input
            id="storage-cdn"
            value={form.aws_public_url_prefix}
            onChange={(e) => setForm({ ...form, aws_public_url_prefix: e.target.value })}
            placeholder="https://cdn.mydomain.com"
            data-testid="storage-aws-cdn"
          />
        </Field>
      </div>

      <div className="flex flex-wrap items-center gap-2 pt-2">
        <Button type="submit" disabled={saving} className="bg-[#ee5e23] hover:bg-[#d24e15] text-white" data-testid="storage-save-btn">
          <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving…' : 'Save storage settings'}
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={testS3}
          disabled={testing}
          data-testid="storage-test-btn"
        >
          {testing ? 'Testing…' : 'Test S3 connection'}
        </Button>
      </div>

      {testResult && (
        <div
          className={`text-sm rounded-md p-3 border ${testResult.ok ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 'bg-red-50 border-red-200 text-red-800'}`}
          data-testid="storage-test-result"
        >
          <b>{testResult.ok ? 'Connection OK' : 'Connection failed'}</b>
          <div className="mt-1 font-mono text-xs whitespace-pre-wrap">{testResult.detail}</div>
        </div>
      )}
    </form>
  );
};

const SitemapsForm = () => {
  const { toast } = useToast();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pinging, setPinging] = useState(false);

  const flagKey = (k) => `${k}_enabled`;

  const load = async () => {
    try {
      const r = await adminApi.getSitemaps();
      setData(r);
    } catch (e) {
      toast({ title: 'Failed to load sitemaps', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const toggle = async (key, next) => {
    const payload = {};
    (data.items || []).forEach((it) => { payload[flagKey(it.key)] = it.key === key ? next : it.enabled; });
    setSaving(true);
    try {
      await adminApi.saveSitemaps(payload);
      toast({ title: `${next ? 'Enabled' : 'Disabled'} ${key} sitemap` });
      await load();
    } catch (e) {
      toast({ title: 'Save failed', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    } finally { setSaving(false); }
  };

  const copy = (url) => { navigator.clipboard?.writeText(url); toast({ title: 'Copied', description: url }); };

  const ping = async () => {
    setPinging(true);
    try {
      const r = await adminApi.pingSitemaps();
      const txt = Object.entries(r.results || {}).map(([k, v]) => {
        const deprecated = v.status === 404 || v.status === 410;
        return `${k}: ${v.ok ? 'OK' : deprecated ? 'deprecated by provider' : (v.error || v.status)}`;
      }).join(' · ');
      toast({ title: 'Notified search engines', description: txt });
    } catch (e) {
      toast({ title: 'Ping failed', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    } finally { setPinging(false); }
  };

  if (loading) return <div className="text-sm text-gray-500">Loading sitemaps…</div>;
  if (!data) return null;

  return (
    <div className="space-y-6" data-testid="sitemaps-form">
      <div>
        <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2"><MapIcon className="w-5 h-5" /> XML Sitemaps</h2>
        <p className="text-xs text-gray-500 mt-1">All sitemaps are generated live from your data — they’re always up to date. Toggle a section off to remove it from the index, copy a URL to submit it in Search Console, or notify search engines below.</p>
      </div>

      {/* Sitemap index */}
      <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <div className="text-sm font-semibold text-gray-900">Sitemap index</div>
            <a href={data.index_url} target="_blank" rel="noreferrer" className="text-xs text-[#2c54b2] hover:underline break-all" data-testid="sitemap-index-url">{data.index_url}</a>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button type="button" size="sm" variant="outline" onClick={() => copy(data.index_url)} data-testid="copy-sitemap-index"><Copy className="w-4 h-4 mr-1" /> Copy</Button>
            <a href={data.index_url} target="_blank" rel="noreferrer"><Button type="button" size="sm" variant="outline"><ExternalLink className="w-4 h-4 mr-1" /> Open</Button></a>
          </div>
        </div>
      </div>

      {/* Child sitemaps */}
      <div className="space-y-3">
        {(data.items || []).map((it) => (
          <div key={it.key} className="border border-gray-200 rounded-lg p-4 flex items-center justify-between gap-3 flex-wrap" data-testid={`sitemap-row-${it.key}`}>
            <div className="min-w-0">
              <div className="text-sm font-semibold text-gray-900 flex items-center gap-2">
                {it.label} Sitemap
                <span className="text-[11px] font-medium bg-blue-50 text-[#2c54b2] rounded-full px-2 py-0.5" data-testid={`sitemap-count-${it.key}`}>{it.count} URLs</span>
              </div>
              <a href={it.url} target="_blank" rel="noreferrer" className="text-xs text-[#2c54b2] hover:underline break-all">{it.url}</a>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <Button type="button" size="sm" variant="outline" onClick={() => copy(it.url)} data-testid={`copy-sitemap-${it.key}`}><Copy className="w-4 h-4 mr-1" /> Copy</Button>
              <a href={it.url} target="_blank" rel="noreferrer"><Button type="button" size="sm" variant="outline"><ExternalLink className="w-4 h-4 mr-1" /> Open</Button></a>
              <Switch checked={it.enabled} disabled={saving} onCheckedChange={(v) => toggle(it.key, v)} data-testid={`sitemap-toggle-${it.key}`} />
            </div>
          </div>
        ))}
      </div>

      {/* Actions */}
      <div className="flex items-center gap-3 flex-wrap pt-2 border-t border-gray-100">
        <Button type="button" onClick={ping} disabled={pinging} className="bg-[#ee5e23] hover:bg-[#d54d18]" data-testid="sitemap-ping-btn">
          <Send className="w-4 h-4 mr-2" /> {pinging ? 'Notifying…' : 'Ping Google & Bing'}
        </Button>
        <Button type="button" variant="outline" onClick={load} data-testid="sitemap-refresh-btn"><RefreshCw className="w-4 h-4 mr-2" /> Refresh counts</Button>
        <a href="https://search.google.com/search-console" target="_blank" rel="noreferrer" className="text-xs text-[#2c54b2] hover:underline inline-flex items-center gap-1"><ExternalLink className="w-3.5 h-3.5" /> Google Search Console</a>
        <a href="https://www.bing.com/webmasters" target="_blank" rel="noreferrer" className="text-xs text-[#2c54b2] hover:underline inline-flex items-center gap-1"><ExternalLink className="w-3.5 h-3.5" /> Bing Webmaster</a>
      </div>
      <p className="text-[11px] text-gray-400">Tip: Google & Bing have retired their automatic “ping” endpoints — the most reliable way to submit is via Search Console / Bing Webmaster (links above). Your sitemap index is also listed in robots.txt so crawlers discover it automatically.</p>
    </div>
  );
};


const HERO_DEFAULTS = {
  badge_text: "Telangana's Largest B2B Marketplace",
  headline: 'Get the Best Quotes for your Business',
  subtitle: 'Tell us what you need and get verified vendors ready to quote within minutes.',
  cta_text: 'Post Your Requirement',
};

const HeroColorField = ({ label, value, onChange, def, testid }) => (
  <div>
    <Label className="text-sm">{label}</Label>
    <div className="mt-1 flex items-center gap-2">
      <input type="color" value={value || def} onChange={(e) => onChange(e.target.value)}
        className="h-9 w-12 border border-gray-300 rounded cursor-pointer shrink-0" data-testid={`${testid}-picker`} />
      <Input value={value || ''} onChange={(e) => onChange(e.target.value)} placeholder={`Default ${def}`}
        className="w-32" data-testid={`${testid}-text`} />
      {value && (
        <Button type="button" variant="ghost" size="sm" onClick={() => onChange('')} data-testid={`${testid}-clear`}>Reset</Button>
      )}
    </div>
  </div>
);

const HeroSelectField = ({ label, value, onChange, options, testid }) => (
  <div>
    <Label className="text-sm">{label}</Label>
    <select value={value || ''} onChange={(e) => onChange(e.target.value)}
      className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2 text-sm bg-white" data-testid={testid}>
      {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  </div>
);

const HERO_STYLE_OPTIONS = [
  { value: '', label: 'Default' },
  { value: 'normal', label: 'Normal' },
  { value: 'bold', label: 'Bold' },
  { value: 'italic', label: 'Italic' },
  { value: 'bold-italic', label: 'Bold Italic' },
];

const HomeHeroForm = () => {
  const { toast } = useToast();
  const [form, setForm] = useState({
    badge_text: '', headline: '', subtitle: '', cta_text: '',
    badge_text_te: '', headline_te: '', subtitle_te: '', cta_text_te: '',
    stats: [],
    badge_size: '', badge_color: '',
    headline_size: '', headline_style: '', headline_color: '',
    subtitle_size: '', subtitle_style: '', subtitle_color: '',
    cta_bg_color: '', cta_text_color: '',
    bg_from_color: '', bg_to_color: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await adminApi.getSettings('hero');
        const v = r.value || {};
        let stats = Array.isArray(v.stats) ? v.stats : [];
        stats = stats.map((s, i) => ({ _key: `k-${i}-${Date.now()}`, ...s }));
        if (!stats.length) {
          // Prefill with the current live stats so the admin edits from real values.
          try {
            const res = await fetch(`${BACKEND_URL}/api/stats`);
            const cur = await res.json();
            stats = (cur || []).map((s) => ({ value: s.value || '', label: s.label || '', value_te: '', label_te: '' }));
          } catch { stats = []; }
        }
        setForm({
          badge_text: v.badge_text || '',
          headline: v.headline || '',
          subtitle: v.subtitle || '',
          cta_text: v.cta_text || '',
          badge_text_te: v.badge_text_te || '',
          headline_te: v.headline_te || '',
          subtitle_te: v.subtitle_te || '',
          cta_text_te: v.cta_text_te || '',
          stats,
          badge_size: v.badge_size || '',
          badge_color: v.badge_color || '',
          headline_size: v.headline_size || '',
          headline_style: v.headline_style || '',
          headline_color: v.headline_color || '',
          subtitle_size: v.subtitle_size || '',
          subtitle_style: v.subtitle_style || '',
          subtitle_color: v.subtitle_color || '',
          cta_bg_color: v.cta_bg_color || '',
          cta_text_color: v.cta_text_color || '',
          bg_from_color: v.bg_from_color || '',
          bg_to_color: v.bg_to_color || '',
        });
      } catch (e) {
        toast({ title: 'Failed to load hero settings', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
      } finally { setLoading(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setStat = (i, key, val) => {
    setForm((f) => {
      const stats = f.stats.map((s, idx) => (idx === i ? { ...s, [key]: val } : s));
      return { ...f, stats };
    });
  };
  const addStat = () => setForm((f) => ({ ...f, stats: [...f.stats, { _key: `k-${Date.now()}`, value: '', label: '', value_te: '', label_te: '' }] }));
  const removeStat = (i) => setForm((f) => ({ ...f, stats: f.stats.filter((_, idx) => idx !== i) }));

  const save = async (e) => {
    e?.preventDefault?.();
    setSaving(true);
    try {
      const payload = {
        ...form,
        stats: form.stats
          .filter((s) => (s.value || '').trim() || (s.label || '').trim())
          .map(({ _key, ...rest }) => rest),
      };
      await adminApi.saveSettings('hero', payload);
      setForm(payload);
      toast({ title: 'Home hero saved', description: 'Reload the home page to see the changes.' });
    } catch (err) {
      toast({ title: 'Save failed', description: err?.response?.data?.detail || err.message, variant: 'destructive' });
    } finally { setSaving(false); }
  };

  if (loading) return <div className="text-gray-500">Loading hero settings…</div>;

  return (
    <form onSubmit={save} className="space-y-6" data-testid="site-settings-hero-form">
      <div>
        <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2"><Home className="w-5 h-5" /> Home Page Hero Section</h2>
        <p className="text-xs text-gray-500 mt-1">
          Edit the blue banner at the top of the home page — badge, headline, subtitle, the stat boxes and the orange button text.
          Leave a field blank to use the built-in default. Telugu fields are shown when a visitor switches the site language to తెలుగు.
        </p>
      </div>

      <section>
        <h3 className="text-sm font-semibold mb-3 text-gray-800">English text</h3>
        <div className="space-y-4">
          <Field label="Badge text (small pill above the headline)" htmlFor="hero_badge">
            <Input id="hero_badge" value={form.badge_text} placeholder={HERO_DEFAULTS.badge_text}
              onChange={(e) => setForm({ ...form, badge_text: e.target.value })} data-testid="hero-badge-input" />
          </Field>
          <Field label="Headline" htmlFor="hero_headline">
            <Input id="hero_headline" value={form.headline} placeholder={HERO_DEFAULTS.headline}
              onChange={(e) => setForm({ ...form, headline: e.target.value })} data-testid="hero-headline-input" />
          </Field>
          <Field label="Subtitle" htmlFor="hero_subtitle">
            <Textarea id="hero_subtitle" rows={2} value={form.subtitle} placeholder={HERO_DEFAULTS.subtitle}
              onChange={(e) => setForm({ ...form, subtitle: e.target.value })} data-testid="hero-subtitle-input" />
          </Field>
          <Field label="Button text (orange CTA)" htmlFor="hero_cta">
            <Input id="hero_cta" value={form.cta_text} placeholder={HERO_DEFAULTS.cta_text}
              onChange={(e) => setForm({ ...form, cta_text: e.target.value })} data-testid="hero-cta-input" />
          </Field>
        </div>
      </section>

      <section className="p-4 border rounded-md bg-gray-50">
        <h3 className="text-sm font-semibold mb-3 text-gray-800">Telugu text (తెలుగు)</h3>
        <p className="text-xs text-gray-500 mb-3">Optional. If blank, Telugu visitors see the English value above (or the built-in Telugu default when that is blank too).</p>
        <div className="space-y-4">
          <Field label="Badge text" htmlFor="hero_badge_te">
            <Input id="hero_badge_te" value={form.badge_text_te} placeholder="తెలంగాణ యొక్క అతిపెద్ద B2B మార్కెట్‌ప్లేస్"
              onChange={(e) => setForm({ ...form, badge_text_te: e.target.value })} data-testid="hero-badge-te-input" />
          </Field>
          <Field label="Headline" htmlFor="hero_headline_te">
            <Input id="hero_headline_te" value={form.headline_te} placeholder="మీ వ్యాపారానికి ఉత్తమ కోట్‌లను పొందండి"
              onChange={(e) => setForm({ ...form, headline_te: e.target.value })} data-testid="hero-headline-te-input" />
          </Field>
          <Field label="Subtitle" htmlFor="hero_subtitle_te">
            <Textarea id="hero_subtitle_te" rows={2} value={form.subtitle_te} placeholder="మీకు ఏమి కావాలో చెప్పండి — నిర్ధారిత విక్రేతలు నిమిషాల్లో కోట్ చేస్తారు."
              onChange={(e) => setForm({ ...form, subtitle_te: e.target.value })} data-testid="hero-subtitle-te-input" />
          </Field>
          <Field label="Button text" htmlFor="hero_cta_te">
            <Input id="hero_cta_te" value={form.cta_text_te} placeholder="మీ అవసరాన్ని పోస్ట్ చేయండి"
              onChange={(e) => setForm({ ...form, cta_text_te: e.target.value })} data-testid="hero-cta-te-input" />
          </Field>
        </div>
      </section>

      <section>
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-sm font-semibold text-gray-800">Stat boxes</h3>
            <p className="text-xs text-gray-500">The number cards shown under the subtitle (e.g. “10 Lakh+ Buyers”). Add up to 8.</p>
          </div>
          <Button type="button" variant="outline" size="sm" onClick={addStat} disabled={form.stats.length >= 8} data-testid="hero-add-stat-btn">
            <Plus className="w-4 h-4 mr-1" /> Add stat
          </Button>
        </div>
        {form.stats.length === 0 && (
          <p className="text-xs text-gray-400 border border-dashed rounded p-3" data-testid="hero-stats-empty">No stats configured — the home page will show the built-in defaults. Click “Add stat” to customise.</p>
        )}
        <div className="space-y-3">
          {form.stats.map((s, i) => (
            <div key={s._key || `stat-${i}`} className="border border-gray-200 rounded-md p-3 bg-white" data-testid={`hero-stat-row-${i}`}>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Field label="Value" htmlFor={`stat_value_${i}`}>
                  <Input id={`stat_value_${i}`} value={s.value || ''} placeholder="10 Lakh+"
                    onChange={(e) => setStat(i, 'value', e.target.value)} data-testid={`hero-stat-value-${i}`} />
                </Field>
                <Field label="Label" htmlFor={`stat_label_${i}`}>
                  <Input id={`stat_label_${i}`} value={s.label || ''} placeholder="Buyers"
                    onChange={(e) => setStat(i, 'label', e.target.value)} data-testid={`hero-stat-label-${i}`} />
                </Field>
                <Field label="Value (తెలుగు)" htmlFor={`stat_value_te_${i}`}>
                  <Input id={`stat_value_te_${i}`} value={s.value_te || ''} placeholder="optional"
                    onChange={(e) => setStat(i, 'value_te', e.target.value)} data-testid={`hero-stat-value-te-${i}`} />
                </Field>
                <Field label="Label (తెలుగు)" htmlFor={`stat_label_te_${i}`}>
                  <Input id={`stat_label_te_${i}`} value={s.label_te || ''} placeholder="optional"
                    onChange={(e) => setStat(i, 'label_te', e.target.value)} data-testid={`hero-stat-label-te-${i}`} />
                </Field>
              </div>
              <div className="flex justify-end mt-2">
                <Button type="button" variant="ghost" size="sm" className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  onClick={() => removeStat(i)} data-testid={`hero-stat-remove-${i}`}>
                  <Trash2 className="w-4 h-4 mr-1" /> Remove
                </Button>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="p-4 border rounded-md">
        <h3 className="text-sm font-semibold mb-1 text-gray-800">Fonts &amp; Colors</h3>
        <p className="text-xs text-gray-500 mb-4">Customise text size, style and colors of the hero section. “Default” / blank keeps the current design.</p>
        <div className="space-y-5">
          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">Badge (pill)</div>
            <div className="grid md:grid-cols-2 gap-4">
              <HeroSelectField label="Font size" value={form.badge_size} testid="hero-badge-size-select"
                onChange={(v) => setForm({ ...form, badge_size: v })}
                options={[{ value: '', label: 'Default (Extra small)' }, { value: 'xs', label: 'Extra small' }, { value: 'sm', label: 'Small' }, { value: 'md', label: 'Medium' }]} />
              <HeroColorField label="Text color" value={form.badge_color} def="#ffffff" testid="hero-badge-color"
                onChange={(v) => setForm({ ...form, badge_color: v })} />
            </div>
          </div>
          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">Headline</div>
            <div className="grid md:grid-cols-3 gap-4">
              <HeroSelectField label="Font size" value={form.headline_size} testid="hero-headline-size-select"
                onChange={(v) => setForm({ ...form, headline_size: v })}
                options={[{ value: '', label: 'Default (Large)' }, { value: 'sm', label: 'Small' }, { value: 'md', label: 'Medium' }, { value: 'lg', label: 'Large' }, { value: 'xl', label: 'Extra large' }]} />
              <HeroSelectField label="Font style" value={form.headline_style} testid="hero-headline-style-select"
                onChange={(v) => setForm({ ...form, headline_style: v })} options={HERO_STYLE_OPTIONS} />
              <HeroColorField label="Text color" value={form.headline_color} def="#ffffff" testid="hero-headline-color"
                onChange={(v) => setForm({ ...form, headline_color: v })} />
            </div>
          </div>
          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">Subtitle</div>
            <div className="grid md:grid-cols-3 gap-4">
              <HeroSelectField label="Font size" value={form.subtitle_size} testid="hero-subtitle-size-select"
                onChange={(v) => setForm({ ...form, subtitle_size: v })}
                options={[{ value: '', label: 'Default (Medium)' }, { value: 'sm', label: 'Small' }, { value: 'md', label: 'Medium' }, { value: 'lg', label: 'Large' }]} />
              <HeroSelectField label="Font style" value={form.subtitle_style} testid="hero-subtitle-style-select"
                onChange={(v) => setForm({ ...form, subtitle_style: v })} options={HERO_STYLE_OPTIONS} />
              <HeroColorField label="Text color" value={form.subtitle_color} def="#dbeafe" testid="hero-subtitle-color"
                onChange={(v) => setForm({ ...form, subtitle_color: v })} />
            </div>
          </div>
          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">Button (CTA)</div>
            <div className="grid md:grid-cols-2 gap-4">
              <HeroColorField label="Background color" value={form.cta_bg_color} def="#ee5e23" testid="hero-cta-bg-color"
                onChange={(v) => setForm({ ...form, cta_bg_color: v })} />
              <HeroColorField label="Text color" value={form.cta_text_color} def="#ffffff" testid="hero-cta-text-color"
                onChange={(v) => setForm({ ...form, cta_text_color: v })} />
            </div>
          </div>
          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">Section background (gradient)</div>
            <div className="grid md:grid-cols-2 gap-4">
              <HeroColorField label="Gradient start (top-left)" value={form.bg_from_color} def="#2c54b2" testid="hero-bg-from-color"
                onChange={(v) => setForm({ ...form, bg_from_color: v })} />
              <HeroColorField label="Gradient end (bottom-right)" value={form.bg_to_color} def="#1e3a8a" testid="hero-bg-to-color"
                onChange={(v) => setForm({ ...form, bg_to_color: v })} />
            </div>
          </div>
        </div>
      </section>

      <div className="flex justify-end pt-2 border-t">
        <Button type="submit" disabled={saving} className="bg-[#ee5e23] hover:bg-[#d54d18]" data-testid="hero-save-btn">
          <Save className="w-4 h-4 mr-2" />{saving ? 'Saving…' : 'Save hero settings'}
        </Button>
      </div>
    </form>
  );
};

const SiteSettingsPanel = () => {
  const [section, setSection] = useState('msg91');
  return (
    <div data-testid="site-settings-panel">
      <div className="flex items-center gap-2 mb-1">
        <SettingsIcon className="w-5 h-5 text-gray-700" />
        <h1 className="text-2xl font-bold text-gray-900">Site Settings</h1>
      </div>
      <p className="text-sm text-gray-600 mb-5">
        Manage 3rd-party integration credentials. Saved values override environment variables and apply instantly without a backend restart.
      </p>

      {section !== 'email' && section !== 'google' && section !== 'footer' && section !== 'branding' && section !== 'seo' && section !== 'nav' && section !== 'pwa' && section !== 'hero' && <ActiveProviderBanner />}

      <div className="flex gap-2 mb-5 border-b overflow-x-auto">
        <button
          onClick={() => setSection('msg91')}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition whitespace-nowrap ${
            section === 'msg91' ? 'border-[#ee5e23] text-[#ee5e23]' : 'border-transparent text-gray-600 hover:text-gray-900'
          }`}
          data-testid="settings-tab-msg91"
        >
          <MessageCircle className="w-4 h-4 inline mr-1" /> MSG91
        </button>
        <button
          onClick={() => setSection('smslogin')}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition whitespace-nowrap ${
            section === 'smslogin' ? 'border-[#ee5e23] text-[#ee5e23]' : 'border-transparent text-gray-600 hover:text-gray-900'
          }`}
          data-testid="settings-tab-smslogin"
        >
          <MessageCircle className="w-4 h-4 inline mr-1" /> SMSLogin (Genfon)
        </button>
        <button
          onClick={() => setSection('email')}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition whitespace-nowrap ${
            section === 'email' ? 'border-[#ee5e23] text-[#ee5e23]' : 'border-transparent text-gray-600 hover:text-gray-900'
          }`}
          data-testid="settings-tab-email"
        >
          <Mail className="w-4 h-4 inline mr-1" /> Email Notifications
        </button>
        <button
          onClick={() => setSection('google')}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition whitespace-nowrap ${
            section === 'google' ? 'border-[#ee5e23] text-[#ee5e23]' : 'border-transparent text-gray-600 hover:text-gray-900'
          }`}
          data-testid="settings-tab-google"
        >
          <KeyRound className="w-4 h-4 inline mr-1" /> Google Sign-In
        </button>
        <button
          onClick={() => setSection('footer')}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition whitespace-nowrap ${
            section === 'footer' ? 'border-[#ee5e23] text-[#ee5e23]' : 'border-transparent text-gray-600 hover:text-gray-900'
          }`}
          data-testid="settings-tab-footer"
        >
          <Layout className="w-4 h-4 inline mr-1" /> Footer
        </button>
        <button
          onClick={() => setSection('branding')}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition whitespace-nowrap ${
            section === 'branding' ? 'border-[#ee5e23] text-[#ee5e23]' : 'border-transparent text-gray-600 hover:text-gray-900'
          }`}
          data-testid="settings-tab-branding"
        >
          <ImageIcon className="w-4 h-4 inline mr-1" /> Branding
        </button>
        <button
          onClick={() => setSection('hero')}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition whitespace-nowrap ${
            section === 'hero' ? 'border-[#ee5e23] text-[#ee5e23]' : 'border-transparent text-gray-600 hover:text-gray-900'
          }`}
          data-testid="settings-tab-hero"
        >
          <Home className="w-4 h-4 inline mr-1" /> Home Hero
        </button>
        <button
          onClick={() => setSection('seo')}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition whitespace-nowrap ${
            section === 'seo' ? 'border-[#ee5e23] text-[#ee5e23]' : 'border-transparent text-gray-600 hover:text-gray-900'
          }`}
          data-testid="settings-tab-seo"
        >
          <SearchIcon className="w-4 h-4 inline mr-1" /> SEO &amp; Meta
        </button>
        <button
          onClick={() => setSection('razorpay')}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition whitespace-nowrap ${
            section === 'razorpay' ? 'border-[#ee5e23] text-[#ee5e23]' : 'border-transparent text-gray-600 hover:text-gray-900'
          }`}
          data-testid="settings-tab-razorpay"
        >
          <CreditCard className="w-4 h-4 inline mr-1" /> Razorpay
        </button>
        <button
          onClick={() => setSection('nav')}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition whitespace-nowrap ${
            section === 'nav' ? 'border-[#ee5e23] text-[#ee5e23]' : 'border-transparent text-gray-600 hover:text-gray-900'
          }`}
          data-testid="settings-tab-nav"
        >
          <Compass className="w-4 h-4 inline mr-1" /> Navigation
        </button>
        <button
          onClick={() => setSection('pwa')}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition whitespace-nowrap ${
            section === 'pwa' ? 'border-[#ee5e23] text-[#ee5e23]' : 'border-transparent text-gray-600 hover:text-gray-900'
          }`}
          data-testid="settings-tab-pwa"
        >
          <Smartphone className="w-4 h-4 inline mr-1" /> PWA
        </button>
        <button
          onClick={() => setSection('inbox')}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition whitespace-nowrap ${
            section === 'inbox' ? 'border-[#ee5e23] text-[#ee5e23]' : 'border-transparent text-gray-600 hover:text-gray-900'
          }`}
          data-testid="settings-tab-inbox"
        >
          <Mail className="w-4 h-4 inline mr-1" /> Inbox
        </button>
        <button
          onClick={() => setSection('storage')}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition whitespace-nowrap ${
            section === 'storage' ? 'border-[#ee5e23] text-[#ee5e23]' : 'border-transparent text-gray-600 hover:text-gray-900'
          }`}
          data-testid="settings-tab-storage"
        >
          <HardDrive className="w-4 h-4 inline mr-1" /> Storage
        </button>
        <button
          onClick={() => setSection('sitemaps')}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition whitespace-nowrap ${
            section === 'sitemaps' ? 'border-[#ee5e23] text-[#ee5e23]' : 'border-transparent text-gray-600 hover:text-gray-900'
          }`}
          data-testid="settings-tab-sitemaps"
        >
          <MapIcon className="w-4 h-4 inline mr-1" /> Sitemaps
        </button>
        <button
          onClick={() => setSection('ai')}
          className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition whitespace-nowrap ${
            section === 'ai' ? 'border-[#ee5e23] text-[#ee5e23]' : 'border-transparent text-gray-600 hover:text-gray-900'
          }`}
          data-testid="settings-tab-ai"
        >
          <Sparkles className="w-4 h-4 inline mr-1" /> AI (Gemini)
        </button>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg p-6">
        {section === 'msg91' && <Msg91Form />}
        {section === 'smslogin' && <SmsLoginForm />}
        {section === 'email' && <EmailForm />}
        {section === 'google' && <GoogleOAuthForm />}
        {section === 'footer' && <FooterForm />}
        {section === 'branding' && <BrandingForm />}
        {section === 'hero' && <HomeHeroForm />}
        {section === 'seo' && <SeoForm />}
        {section === 'razorpay' && <RazorpayForm />}
        {section === 'nav' && <NavForm />}
        {section === 'pwa' && <PwaForm />}
        {section === 'inbox' && <InboxForm />}
        {section === 'storage' && <StorageForm />}
        {section === 'sitemaps' && <SitemapsForm />}
        {section === 'ai' && <AiForm />}
      </div>
    </div>
  );
};

export default SiteSettingsPanel;
